// SPDX-License-Identifier: AGPL-3.0-or-later

import {showGenericErrorModal} from '@app/features/app/components/alerts/GenericErrorModalCommands';
import Authentication from '@app/features/auth/state/Authentication';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import GatewayConnection from '@app/features/gateway/transport/GatewayConnection';
import type {GatewayErrorData} from '@app/features/gateway/transport/GatewaySocket';
import type {GuildReadyData} from '@app/features/gateway/types/GatewayGuildTypes';
import type {VoiceState} from '@app/features/gateway/types/GatewayVoiceTypes';
import GuildMatureContentAgree from '@app/features/guild/state/GuildMatureContentAgree';
import Guilds from '@app/features/guild/state/Guilds';
import {SOMETHING_WENT_WRONG_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import Keybind from '@app/features/input/state/InputKeybind';
import GuildMembers from '@app/features/member/state/GuildMembers';
import * as NavigationCommands from '@app/features/navigation/commands/NavigationCommands';
import {SoundType} from '@app/features/notification/utils/SoundUtils';
import type {VoiceEngineEvent} from '@app/features/platform/types/Electron';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as SoundCommands from '@app/features/ui/commands/SoundCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import Idle from '@app/features/ui/state/Idle';
import {getElectronAPI} from '@app/features/ui/utils/NativeUtils';
import Users from '@app/features/user/state/Users';
import {getStreamKey, parseStreamKey} from '@app/features/voice/components/StreamKeys';
import {voiceStatsDB} from '@app/features/voice/diagnostics/VoiceStatsDB';
import AdaptiveScreenShareEngine from '@app/features/voice/engine/AdaptiveScreenShareEngine';
import {
	createMediaEngineFacadeSnapshot,
	type MediaEngineFacadeEvent,
	type MediaEngineFacadePendingSessionRestore,
	type MediaEngineFacadeSnapshot,
	selectMediaEngineConnectPreflightDecision,
	selectMediaEngineConnectRequestDecision,
	selectMediaEngineGatewayErrorDecision,
	shouldRunMediaEngineDeferredDisconnect,
	transitionMediaEngineFacadeSnapshot,
} from '@app/features/voice/engine/MediaEngineFacadeStateMachine';
import {
	AFK_CHECK_INTERVAL_MS,
	CLAIM_YOUR_ACCOUNT_TO_JOIN_THIS_VOICE_CHANNEL_DESCRIPTOR,
	CLAIM_YOUR_ACCOUNT_TO_JOIN_VOICE_CHANNELS_YOU_DESCRIPTOR,
	CLAIM_YOUR_ACCOUNT_TO_START_OR_JOIN_1_DESCRIPTOR,
	DEFERRED_DISCONNECT_TIMEOUT_MS,
	RECONNECT_SUCCEEDED_PICK_A_SCREEN_AGAIN_IF_YOU_DESCRIPTOR,
	VOICE_CHANNEL_NO_LONGER_AVAILABLE_DESCRIPTOR,
	VOICE_CONNECTION_LIMIT_REACHED_DESCRIPTOR,
	YOU_CAN_T_JOIN_WHILE_YOU_RE_ON_DESCRIPTOR,
} from '@app/features/voice/engine/media_engine_facade/shared';
import {
	getVoiceEngine,
	isNativeVoiceEngineSelected,
	isNativeVoiceEngineSelectionPending,
	shouldUseNativeVoiceEngine,
	suppressNativeVoiceEngineForFallback,
} from '@app/features/voice/engine/native_voice_engine/getVoiceEngine';
import NativeInboundVideoManager from '@app/features/voice/engine/native_voice_engine/NativeInboundVideoManager';
import {bindNativeVoiceDeviceSync} from '@app/features/voice/engine/native_voice_engine/NativeVoiceDeviceSync';
import NativeVoiceE2EEStore from '@app/features/voice/engine/native_voice_engine/NativeVoiceE2EEStore';
import NativeVoiceStatsStore from '@app/features/voice/engine/native_voice_engine/NativeVoiceStatsStore';
import {
	getNativeVoiceEngineConnectionEventAction,
	isFacadeOwnedConnectionEvent,
	mapNativeVoiceEngineEvent,
	type NativeVoiceEngineEventManagers,
} from '@app/features/voice/engine/native_voice_engine/nativeVoiceEngineEventMapper';
import {getScreenShareCaptureDiagnosticSnapshot} from '@app/features/voice/engine/ScreenShareCaptureDiagnostics';
import ScreenShareCodecNegotiation, {
	buildLocalCodecAdvertisements,
	getScreenShareCodecPreferenceOrder,
	SCREEN_SHARE_CODEC_NEGOTIATION_TOPIC,
} from '@app/features/voice/engine/ScreenShareCodecNegotiation';
import ScreenSharePublicationMigration, {
	SCREEN_SHARE_PUBLICATION_MIGRATION_TOPIC,
} from '@app/features/voice/engine/ScreenSharePublicationMigration';
import {shouldMoveToGuildAfkChannel} from '@app/features/voice/engine/VoiceAfkTracking';
import {
	getEffectiveSelfMuteForVoiceStatePayload,
	isPushToMuteActive,
	isPushToTalkActive,
	type VoiceMuteReason,
} from '@app/features/voice/engine/VoiceAudioManager';
import {
	checkChannelLimit,
	checkMultipleConnections,
	sendVoiceStateConnect,
	sendVoiceStateDisconnect,
} from '@app/features/voice/engine/VoiceChannelConnector';
import type {VoiceServerUpdateData} from '@app/features/voice/engine/VoiceConnectionManager';
import VoiceConnectionManager from '@app/features/voice/engine/VoiceConnectionManager';
import VoiceDebugLoggingManager from '@app/features/voice/engine/VoiceDebugLoggingManager';
import VoiceDevicePermissionState from '@app/features/voice/engine/VoiceDevicePermissionState';
import {getEffectiveAudioState} from '@app/features/voice/engine/VoiceEffectiveAudioState';
import type {NormalizedVoiceState} from '@app/features/voice/engine/VoiceGatewayStateMachine';
import {
	noteLocalVoiceActivity,
	noteLocalVoiceActivityFromSnapshot,
} from '@app/features/voice/engine/VoiceIdleActivityBridge';
import VoiceMediaManager from '@app/features/voice/engine/VoiceMediaManager';
import VoiceMediaStateCoordinator from '@app/features/voice/engine/VoiceMediaStateCoordinator';
import {bindOutputDeviceSync} from '@app/features/voice/engine/VoiceOutputDeviceSync';
import type {LivekitParticipantSnapshot} from '@app/features/voice/engine/VoiceParticipantManager';
import VoiceParticipantManager from '@app/features/voice/engine/VoiceParticipantManager';
import VoicePermissionManager from '@app/features/voice/engine/VoicePermissionManager';
import VoiceRemoteSpeakingLevelManager from '@app/features/voice/engine/VoiceRemoteSpeakingLevelManager';
import {bindRoomEvents} from '@app/features/voice/engine/VoiceRoomEventBinder';
import type {
	DeviceScreenShareCaptureOptions,
	ScreenShareReconnectSnapshot,
} from '@app/features/voice/engine/VoiceScreenShareManager';
import VoiceStateManager from '@app/features/voice/engine/VoiceStateManager';
import {VoiceStateSyncManager} from '@app/features/voice/engine/VoiceStateSyncManager';
import type {VoiceStateSyncPartial, VoiceStateSyncPayload} from '@app/features/voice/engine/VoiceStateSyncStateMachine';
import type {
	LatencyDataPoint,
	PerTrackStats,
	TransportInfo,
	VoiceStats,
	VoiceStatsSample,
} from '@app/features/voice/engine/VoiceStatsManager';
import {VoiceStatsManager} from '@app/features/voice/engine/VoiceStatsManager';
import {
	normalizeStreamGuildId,
	replaceWatchedStreamKeys,
	stopWatchingStreamKey,
} from '@app/features/voice/engine/VoiceStreamWatchState';
import {normalizeViewerStreamKeys} from '@app/features/voice/engine/VoiceStreamWatchStateMachine';
import VoiceSubscriptionManager from '@app/features/voice/engine/VoiceSubscriptionManager';
import {asVoiceTrackSource, VoiceTrackSource} from '@app/features/voice/engine/VoiceTrackSource';
import type {NativeScreenShareOptions} from '@app/features/voice/engine/voice_screen_share_manager/DisplayMediaCapture';
import CallMediaPrefs from '@app/features/voice/state/CallMediaPrefs';
import {computeChannelE2EEStatus} from '@app/features/voice/state/ChannelE2EEStatus';
import LocalVoiceState from '@app/features/voice/state/LocalVoiceState';
import MediaPermission from '@app/features/voice/state/MediaPermission';
import ParticipantVolume from '@app/features/voice/state/ParticipantVolume';
import StreamAudioPrefs from '@app/features/voice/state/StreamAudioPrefs';
import VoiceCallLayout from '@app/features/voice/state/VoiceCallLayout';
import VoiceSessionRestore, {
	VOICE_SESSION_RESTORE_HEARTBEAT_MS,
	type VoiceSessionRestoreSnapshot,
} from '@app/features/voice/state/VoiceSessionRestore';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {getCodecCapabilityReport} from '@app/features/voice/utils/CodecCapabilityDetector';
import {getGpuEncoderReportSync, loadGpuEncoderReport} from '@app/features/voice/utils/GpuEncoderCapabilities';
import {getNativeAudioCaptureDiagnosticState} from '@app/features/voice/utils/NativeAudioCaptureBridge';
import {ensureNativePermission} from '@app/features/voice/utils/NativePermissions';
import {getOpenH264StatusSync, loadOpenH264Status} from '@app/features/voice/utils/OpenH264Status';
import {areOrderedStringArraysEqual} from '@app/features/voice/utils/StringArrayUtils';
import {
	getVideoDecoderExclusionsSync,
	loadVideoDecoderExclusions,
} from '@app/features/voice/utils/VideoDecoderCapabilities';
import {voiceDeviceManager} from '@app/features/voice/utils/VoiceDeviceManager';
import {
	isVoiceServerMuteActive,
	resolveVoiceStateSelfMute,
	shouldPrepareMicrophoneForVoiceConnect,
} from '@app/features/voice/utils/VoicePermissionUtils';
import type {NativeAudioStartOptions} from '@app/types/electron.d';
import {ME} from '@fluxer/constants/src/AppConstants';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {type I18n, i18n as linguiI18n} from '@lingui/core';
import {
	type Participant,
	type Room,
	type ScreenShareCaptureOptions,
	Track,
	type TrackPublishOptions,
} from 'livekit-client';
import {computed, makeObservable, reaction} from 'mobx';

const logger = new Logger('MediaEngineFacade');
const MAX_DIAGNOSTIC_LINUX_AUDIO_TARGETS = 200;
const MAX_DIAGNOSTIC_PIPEWIRE_GRAPH_RECORDS = 500;
const CAMERA_USB_ID_SUFFIX_RE = /\s*\([0-9a-f]{4}:[0-9a-f]{4}\)\s*$/i;

function normalizeCameraLabel(label: string): string {
	return label.trim().toLowerCase().replace(CAMERA_USB_ID_SUFFIX_RE, '').replace(/\s+/g, ' ');
}

function cameraLabelsMatch(browserLabel: string, nativeLabel: string): boolean {
	const left = normalizeCameraLabel(browserLabel);
	const right = normalizeCameraLabel(nativeLabel);
	if (!left || !right) return false;
	return left === right || left.includes(right) || right.includes(left);
}

function canUseCameraDeviceIdDirectly(deviceId: string): boolean {
	return /^\d+$/.test(deviceId.trim());
}

async function resolveNativeCameraDeviceId(deviceId?: string): Promise<string | undefined> {
	if (!deviceId || deviceId === 'default') return undefined;
	try {
		const browserDevices = await voiceDeviceManager.ensureDevices({requestPermissions: false, forceRefresh: true});
		const selectedBrowserDevice = browserDevices.videoDevices.find((device) => device.deviceId === deviceId);
		const browserLabel = selectedBrowserDevice?.label?.trim();
		if (!browserLabel) {
			return canUseCameraDeviceIdDirectly(deviceId) ? deviceId : undefined;
		}
		const nativeDevices = await getVoiceEngine().listCameraDevices();
		const matchedNativeDevice = nativeDevices.find(
			(device) => cameraLabelsMatch(browserLabel, device.label) || cameraLabelsMatch(browserLabel, device.description),
		);
		if (matchedNativeDevice) {
			return matchedNativeDevice.deviceId;
		}
		logger.warn('Native camera device mapping fell back to default camera', {
			browserLabel,
			nativeDeviceCount: nativeDevices.length,
		});
	} catch (error) {
		logger.warn('Failed to map browser camera device to native camera device', {deviceId, error});
	}
	return canUseCameraDeviceIdDirectly(deviceId) ? deviceId : undefined;
}

function voiceDiagnosticError(error: unknown): Record<string, unknown> {
	if (error instanceof Error) {
		return {
			name: error.name,
			message: error.message,
			stack: error.stack,
		};
	}
	return {message: String(error)};
}

function limitLinuxAudioTargets(result: unknown): unknown {
	if (!result || typeof result !== 'object' || !('targets' in result)) return result;
	const record = result as {targets?: unknown};
	if (!Array.isArray(record.targets)) return result;
	return {
		...result,
		targets: record.targets.slice(0, MAX_DIAGNOSTIC_LINUX_AUDIO_TARGETS),
		targetCount: record.targets.length,
		truncated: record.targets.length > MAX_DIAGNOSTIC_LINUX_AUDIO_TARGETS,
	};
}

function limitGraphArray(record: Record<string, unknown>, key: string): Record<string, unknown> {
	const value = record[key];
	if (!Array.isArray(value)) return record;
	return {
		...record,
		[key]: value.slice(0, MAX_DIAGNOSTIC_PIPEWIRE_GRAPH_RECORDS),
		[`${key}Count`]: value.length,
		[`${key}Truncated`]: value.length > MAX_DIAGNOSTIC_PIPEWIRE_GRAPH_RECORDS,
	};
}

function limitPipewireRoutingGraph(graph: unknown): unknown {
	if (!graph || typeof graph !== 'object') return graph;
	let limited = graph as Record<string, unknown>;
	limited = limitGraphArray(limited, 'nodes');
	limited = limitGraphArray(limited, 'ports');
	limited = limitGraphArray(limited, 'ownedLinks');
	return limited;
}

function limitPipewireRoutingGraphResult(result: unknown): unknown {
	if (!result || typeof result !== 'object') return result;
	const record = result as Record<string, unknown>;
	if ('graph' in record) {
		return {...record, graph: limitPipewireRoutingGraph(record.graph)};
	}
	if (Array.isArray(record.graphs)) {
		return {
			...record,
			graphs: record.graphs.map((entry) =>
				entry && typeof entry === 'object'
					? {...entry, graph: limitPipewireRoutingGraph((entry as Record<string, unknown>).graph)}
					: entry,
			),
		};
	}
	return result;
}

function hasPipewireRoutingGraph(result: unknown): boolean {
	if (!result || typeof result !== 'object') return false;
	const record = result as Record<string, unknown>;
	if (record.ok !== true) return false;
	if (record.graph && typeof record.graph === 'object') return true;
	return (
		Array.isArray(record.graphs) &&
		record.graphs.some((entry) => {
			if (!entry || typeof entry !== 'object') return false;
			const graph = (entry as Record<string, unknown>).graph;
			return Boolean(graph && typeof graph === 'object');
		})
	);
}

async function createVoiceAudioDiagnosticsSnapshot(): Promise<Record<string, unknown>> {
	const electron = getElectronAPI();
	const [
		nativeAudioAvailability,
		nativeAudioApplications,
		nativeAudioRoutingGraph,
		virtmicAvailability,
		virtmicTargets,
		virtmicRoutingGraph,
	] = await Promise.all([
		electron?.nativeAudio
			? electron.nativeAudio.getAvailability().catch((error) => ({error: voiceDiagnosticError(error)}))
			: Promise.resolve(null),
		electron?.nativeAudio
			? electron.nativeAudio.listAudibleApplications().catch((error) => ({error: voiceDiagnosticError(error)}))
			: Promise.resolve(null),
		electron?.nativeAudio
			? electron.nativeAudio.getRoutingGraph().catch((error) => ({error: voiceDiagnosticError(error)}))
			: Promise.resolve(null),
		electron?.virtmic
			? electron.virtmic.getAvailability().catch((error) => ({error: voiceDiagnosticError(error)}))
			: Promise.resolve(null),
		electron?.virtmic
			? electron.virtmic.listTargets({granular: true}).catch((error) => ({error: voiceDiagnosticError(error)}))
			: Promise.resolve(null),
		electron?.virtmic
			? electron.virtmic.getRoutingGraph().catch((error) => ({error: voiceDiagnosticError(error)}))
			: Promise.resolve(null),
	]);
	return {
		platform: electron?.platform ?? null,
		nativeAudio: {
			availability: nativeAudioAvailability,
			audibleApplications: nativeAudioApplications,
			capture: getNativeAudioCaptureDiagnosticState(),
			pipewireRoutingGraph: limitPipewireRoutingGraphResult(nativeAudioRoutingGraph),
		},
		linuxRouting: {
			virtmicAvailability,
			pipewireNodeInventory: limitLinuxAudioTargets(virtmicTargets),
			pipewireRoutingGraph: limitPipewireRoutingGraphResult(virtmicRoutingGraph),
			activeLinkGraphExported:
				hasPipewireRoutingGraph(virtmicRoutingGraph) || hasPipewireRoutingGraph(nativeAudioRoutingGraph),
			routingSettings: {
				screenShareAudioSourceMode: VoiceSettings.getScreenShareAudioSourceMode(),
				screenShareAudioIncludeSources: VoiceSettings.getScreenShareAudioIncludeSources(),
				screenShareAudioExcludeSources: VoiceSettings.getScreenShareAudioExcludeSources(),
				shareAppAudio: VoiceSettings.getShareAppAudio(),
				shareDesktopAudio: VoiceSettings.getShareDesktopAudio(),
			},
		},
	};
}

interface ConnectToVoiceChannelOptions {
	skipChannelGate?: boolean;
	deferNavigationUntilConnected?: boolean;
	initialViewerStreamKeys?: Array<string>;
}

interface ConnectDirectlyOptions {
	deferNavigationUntilConnected?: boolean;
	initialViewerStreamKeys?: Array<string>;
}

class MediaEngineFacade {
	private statsManager: VoiceStatsManager;
	private afkIntervalId: NodeJS.Timeout | null = null;
	private outputDeviceSyncDisposer: (() => void) | null = null;
	private audioPreferencesSyncDisposer: (() => void) | null = null;
	private voiceStateSync = new VoiceStateSyncManager();
	private i18n: I18n | null = linguiI18n;
	private pendingServerDisconnectTimeout: NodeJS.Timeout | null = null;
	private facadeSnapshot: MediaEngineFacadeSnapshot = createMediaEngineFacadeSnapshot();
	private disconnectPromise: Promise<void> | null = null;
	private nativeVoiceEngineEventDisposer: (() => void) | null = null;
	private nativeVoiceDeviceSyncDisposer: (() => void) | null = null;
	private nativeVoiceDataProtocolDisposer: (() => void) | null = null;

	constructor() {
		this.statsManager = new VoiceStatsManager();
		makeObservable(this, {
			room: computed,
			guildId: computed,
			channelId: computed,
			connectionId: computed,
			connected: computed,
			connecting: computed,
			voiceServerEndpoint: computed,
			participants: computed,
			connectionVoiceStates: computed,
			currentLatency: computed,
			averageLatency: computed,
			latencyHistory: computed,
			voiceStats: computed,
			perTrackStats: computed,
			statsTimeSeries: computed,
			publisherTransport: computed,
			subscriberTransport: computed,
			reconnectionCount: computed,
			displayLatency: computed,
			estimatedLatency: computed,
		});
		this.initializeLocalAudioStateSync();
		this.initializeVoiceSessionRestoreSync();
		this.initializeE2EEStatusSync();
		(
			window as typeof window & {
				_mediaEngine?: MediaEngineFacade;
			}
		)._mediaEngine = this;
		logger.debug('MediaEngineFacade initialized');
	}

	setI18n(instance: I18n): void {
		this.i18n = instance;
	}

	private transitionFacadeState(event: MediaEngineFacadeEvent): void {
		this.facadeSnapshot = transitionMediaEngineFacadeSnapshot(this.facadeSnapshot, event);
	}

	private get pendingSessionRestore(): MediaEngineFacadePendingSessionRestore | null {
		return this.facadeSnapshot.context.pendingSessionRestore;
	}

	private get pendingUserMove(): {guildId: string | null; channelId: string} | null {
		return this.facadeSnapshot.context.pendingUserMove;
	}

	private get pendingServerDisconnectConnectionId(): string | null {
		return this.facadeSnapshot.context.pendingServerDisconnectConnectionId;
	}

	private get pendingScreenShareReconnect(): ScreenShareReconnectSnapshot | null {
		return this.facadeSnapshot.context.pendingScreenShareReconnect as ScreenShareReconnectSnapshot | null;
	}

	get room(): Room | null {
		return VoiceConnectionManager.room;
	}

	get guildId(): string | null {
		return VoiceConnectionManager.guildId;
	}

	get channelId(): string | null {
		return VoiceConnectionManager.channelId;
	}

	get connectionId(): string | null {
		return VoiceConnectionManager.connectionId;
	}

	get connected(): boolean {
		return VoiceConnectionManager.connected;
	}

	get connecting(): boolean {
		return VoiceConnectionManager.connecting;
	}

	get voiceServerEndpoint(): string | null {
		return VoiceConnectionManager.voiceServerEndpoint;
	}

	get participants(): Readonly<Record<string, LivekitParticipantSnapshot>> {
		return VoiceParticipantManager.participants;
	}

	get connectionVoiceStates(): Readonly<Record<string, NormalizedVoiceState>> {
		return VoiceStateManager.getConnectionVoiceStates();
	}

	get currentLatency(): number | null {
		return this.statsManager.currentLatency;
	}

	get averageLatency(): number | null {
		return this.statsManager.averageLatency;
	}

	get latencyHistory(): Array<LatencyDataPoint> {
		return this.statsManager.latencyHistory;
	}

	get voiceStats(): VoiceStats {
		return this.statsManager.voiceStats;
	}

	get perTrackStats(): Array<PerTrackStats> {
		return this.statsManager.perTrackStats;
	}

	get statsTimeSeries(): Array<VoiceStatsSample> {
		return this.statsManager.statsTimeSeries;
	}

	get publisherTransport(): TransportInfo | null {
		return this.statsManager.publisherTransport;
	}

	get subscriberTransport(): TransportInfo | null {
		return this.statsManager.subscriberTransport;
	}

	get reconnectionCount(): number {
		return this.statsManager.reconnectionCount;
	}

	get estimatedLatency(): number | null {
		return this.statsManager.estimatedLatency;
	}

	get displayLatency(): number | null {
		return this.statsManager.displayLatency;
	}

	private initializeLocalAudioStateSync(): void {
		reaction(
			() => ({
				selfMute: LocalVoiceState.getSelfMute(),
				selfDeaf: LocalVoiceState.getSelfDeaf(),
			}),
			(currentState, previousState) => {
				logger.debug('Local audio state changed, reconciling active voice session', {
					previousState,
					currentState,
				});
				void this.reconcileLocalAudioState('local audio state change');
			},
			{
				name: 'MediaEngineFacade-localAudioStateSync',
			},
		);
	}

	private initializeE2EEStatusSync(): void {
		reaction(
			() => {
				if (!VoiceConnectionManager.connected) return null;
				const channelId = VoiceConnectionManager.channelId;
				const guildId = VoiceConnectionManager.guildId;
				if (!channelId) return null;
				return computeChannelE2EEStatus(guildId, channelId);
			},
			(status) => {
				const room = VoiceConnectionManager.room;
				if (!room) return;
				if (status === 'encrypted') {
					void room.setE2EEEnabled(true).catch((error) => {
						logger.warn('Failed to enable E2EE on room', error);
					});
				} else if (status === 'broken') {
					void room.setE2EEEnabled(false).catch((error) => {
						logger.warn('Failed to disable E2EE on room after capability mix detected', error);
					});
				}
			},
			{
				name: 'MediaEngineFacade-e2eeStatusSync',
			},
		);
	}

	private saveCurrentVoiceSessionRestoreSnapshot(): void {
		const currentUserId = Authentication?.currentUserId ?? null;
		const connected = VoiceConnectionManager.connected;
		const guildId = VoiceConnectionManager.guildId;
		const channelId = VoiceConnectionManager.channelId;
		if (!connected || !currentUserId || !channelId) {
			return;
		}
		VoiceSessionRestore.saveSnapshot({
			userId: currentUserId,
			guildId,
			channelId,
			selfVideo: LocalVoiceState.getSelfVideo(),
			selfStream: LocalVoiceState.getSelfStream(),
		});
	}

	private initializeVoiceSessionRestoreSync(): void {
		reaction(
			() => ({
				currentUserId: Authentication?.currentUserId ?? null,
				connected: VoiceConnectionManager.connected,
				guildId: VoiceConnectionManager.guildId,
				channelId: VoiceConnectionManager.channelId,
				selfVideo: LocalVoiceState.getSelfVideo(),
				selfStream: LocalVoiceState.getSelfStream(),
			}),
			() => {
				this.saveCurrentVoiceSessionRestoreSnapshot();
			},
			{
				name: 'MediaEngineFacade-voiceSessionRestoreSync',
			},
		);
		const saveCurrentSnapshot = () => this.saveCurrentVoiceSessionRestoreSnapshot();
		window.setInterval(saveCurrentSnapshot, VOICE_SESSION_RESTORE_HEARTBEAT_MS);
		window.addEventListener('pagehide', saveCurrentSnapshot);
		window.addEventListener('beforeunload', saveCurrentSnapshot);
	}

	private resolveVoiceConnectChannel(guildId: string | null, channelId: string): Channel | null {
		const channel = Channels.getChannel(channelId) ?? null;
		if (!channel) return null;
		if (channel.type === ChannelTypes.GUILD_VOICE) {
			if (guildId && channel.guildId !== guildId) return null;
			return channel;
		}
		if (guildId) return null;
		if (channel.type === ChannelTypes.DM || channel.type === ChannelTypes.GROUP_DM) {
			return channel;
		}
		return null;
	}

	private showVoiceErrorModal(message: string, dataFlx: string): void {
		if (!this.i18n) return;
		showGenericErrorModal({
			title: this.i18n._(SOMETHING_WENT_WRONG_DESCRIPTOR),
			message,
			dataFlx,
		});
	}

	private showVoiceChannelUnavailableErrorModal(): void {
		if (!this.i18n) return;
		this.showVoiceErrorModal(
			this.i18n._(VOICE_CHANNEL_NO_LONGER_AVAILABLE_DESCRIPTOR),
			'voice.media-engine-facade.channel-unavailable-error-modal',
		);
	}

	private clearUnavailableVoiceTarget(
		channelId: string,
		reason: 'preflight' | 'gateway-error' | 'channel-delete',
		options: {showToast?: boolean} = {},
	): void {
		logger.info('Clearing unavailable voice target', {channelId, reason});
		this.transitionFacadeState({type: 'unavailableTarget.clear', channelId});
		VoiceSessionRestore.clearSnapshotForChannel(channelId);
		VoiceConnectionManager.forgetReconnectChannel(channelId);
		if (options.showToast) {
			this.showVoiceChannelUnavailableErrorModal();
		}
	}

	handleChannelDelete(channelId: string): void {
		this.clearUnavailableVoiceTarget(channelId, 'channel-delete');
		if (VoiceConnectionManager.channelId === channelId) {
			void this.disconnectFromVoiceChannel('server');
		}
	}

	async connectToVoiceChannel(
		guildId: string | null,
		channelId: string,
		options: ConnectToVoiceChannelOptions = {},
	): Promise<void> {
		VoiceCallLayout.reset();
		const voiceChannel = this.resolveVoiceConnectChannel(guildId, channelId);
		const resolvedGuildId = voiceChannel ? (guildId ?? voiceChannel.guildId ?? null) : guildId;
		const currentUserId = Authentication.currentUserId;
		const isTimedOut =
			voiceChannel && resolvedGuildId && currentUserId
				? (GuildMembers.getMember(resolvedGuildId, currentUserId)?.isTimedOut() ?? false)
				: false;
		const currentUser = Users.getCurrentUser();
		const isUnclaimed = !(currentUser?.isClaimed() ?? false);
		const guild = resolvedGuildId ? Guilds.getGuild(resolvedGuildId) : null;
		const isOwner = guild?.isOwner(currentUserId) ?? false;
		const channel = voiceChannel ?? Channels.getChannel(channelId);
		const blockedByMatureGate =
			Boolean(voiceChannel) &&
			!options.skipChannelGate &&
			GuildMatureContentAgree.shouldShowGate({
				channelId,
				guildId: resolvedGuildId,
			});
		const preflightDecision = selectMediaEngineConnectPreflightDecision({
			targetAvailable: Boolean(voiceChannel),
			isTimedOut: Boolean(isTimedOut),
			isUnclaimed,
			isGuildOwner: resolvedGuildId ? isOwner : true,
			isDirectMessage: channel?.type === ChannelTypes.DM,
			hasGatewaySocket: Boolean(GatewayConnection.socket),
			blockedByMatureGate,
			channelLimitAllowed: true,
		});
		if (preflightDecision.type === 'cleanup-unavailable-target') {
			this.clearUnavailableVoiceTarget(channelId, 'preflight', {showToast: preflightDecision.showToast});
			return;
		}
		if (preflightDecision.type === 'toast') {
			if (!this.i18n) {
				throw new Error('MediaEngineFacade: i18n not initialized');
			}
			const descriptor =
				preflightDecision.reason === 'timed-out'
					? YOU_CAN_T_JOIN_WHILE_YOU_RE_ON_DESCRIPTOR
					: preflightDecision.reason === 'claim-account-direct'
						? CLAIM_YOUR_ACCOUNT_TO_START_OR_JOIN_1_DESCRIPTOR
						: CLAIM_YOUR_ACCOUNT_TO_JOIN_VOICE_CHANNELS_YOU_DESCRIPTOR;
			this.showVoiceErrorModal(
				this.i18n._(descriptor),
				`voice.media-engine-facade.preflight-${preflightDecision.reason}-error-modal`,
			);
			return;
		}
		if (preflightDecision.type === 'abort') {
			if (preflightDecision.reason === 'missing-gateway-socket') {
				logger.warn('No socket');
			}
			return;
		}
		if (preflightDecision.type === 'navigate-channel-gate') {
			NavigationCommands.selectChannel(resolvedGuildId ?? undefined, channelId);
			return;
		}
		const channelLimitAllowed = checkChannelLimit(resolvedGuildId, channelId);
		const channelLimitDecision = selectMediaEngineConnectPreflightDecision({
			targetAvailable: true,
			isTimedOut: false,
			isUnclaimed: false,
			isGuildOwner: true,
			isDirectMessage: false,
			hasGatewaySocket: true,
			blockedByMatureGate: false,
			channelLimitAllowed,
		});
		if (channelLimitDecision.type === 'abort') {
			return;
		}
		this.voiceStateSync.reset();
		const shouldProceed = checkMultipleConnections(
			resolvedGuildId,
			channelId,
			async () =>
				this.connectDirectly(resolvedGuildId, channelId, {
					deferNavigationUntilConnected: options.deferNavigationUntilConnected,
					initialViewerStreamKeys: options.initialViewerStreamKeys,
				}),
			() =>
				this.connectDirectly(resolvedGuildId, channelId, {
					deferNavigationUntilConnected: options.deferNavigationUntilConnected,
					initialViewerStreamKeys: options.initialViewerStreamKeys,
				}),
			() => VoiceConnectionManager.clearInFlightConnect(),
		);
		if (!shouldProceed) return;
		await this.connectDirectly(resolvedGuildId, channelId, {
			deferNavigationUntilConnected: options.deferNavigationUntilConnected,
			initialViewerStreamKeys: options.initialViewerStreamKeys,
		});
	}

	private async connectDirectly(
		guildId: string | null,
		channelId: string,
		options: ConnectDirectlyOptions = {},
	): Promise<void> {
		VoiceCallLayout.reset();
		const initialDecision = selectMediaEngineConnectRequestDecision(this.facadeSnapshot, {
			guildId,
			channelId,
			connected: VoiceConnectionManager.connected,
			connecting: VoiceConnectionManager.connecting,
			currentGuildId: VoiceConnectionManager.guildId,
			currentChannelId: VoiceConnectionManager.channelId,
		});
		if (initialDecision.type === 'noop') return;
		if (
			shouldPrepareMicrophoneForVoiceConnect({
				guildId,
				channelId,
				selfMute: LocalVoiceState.getSelfMute(),
				selfDeaf: LocalVoiceState.getSelfDeaf(),
			})
		) {
			const canStartVoiceConnect = await VoiceMediaManager.prepareMicrophonePermissionForConnect();
			if (!canStartVoiceConnect) return;
		}
		const connectDecision = selectMediaEngineConnectRequestDecision(this.facadeSnapshot, {
			guildId,
			channelId,
			connected: VoiceConnectionManager.connected,
			connecting: VoiceConnectionManager.connecting,
			currentGuildId: VoiceConnectionManager.guildId,
			currentChannelId: VoiceConnectionManager.channelId,
		});
		if (connectDecision.type === 'noop') return;
		if (connectDecision.type === 'move-user') {
			this.transitionFacadeState({
				type: 'userMove.requested',
				guildId: connectDecision.guildId,
				channelId: connectDecision.channelId,
			});
			await this.disconnectForChannelMove('user');
		}
		this.voiceStateSync.reset();
		if (!VoiceConnectionManager.startConnection(guildId, channelId)) return;
		const initialViewerStreamKeys = replaceWatchedStreamKeys(options.initialViewerStreamKeys ?? [], {
			sync: false,
		}).keys;
		if (!options.deferNavigationUntilConnected) {
			this.navigateToVoiceChannel(guildId, channelId);
		}
		sendVoiceStateConnect(guildId, channelId, initialViewerStreamKeys);
	}

	async restoreVoiceSession(
		snapshot: VoiceSessionRestoreSnapshot,
		options?: {
			restoreVideo?: boolean;
			restoreStream?: boolean;
		},
	): Promise<void> {
		this.prepareVoiceSessionRestore(snapshot, options);
		await this.connectToVoiceChannel(snapshot.guildId, snapshot.channelId, {deferNavigationUntilConnected: true});
		if (
			!VoiceConnectionManager.connecting &&
			!(
				VoiceConnectionManager.connected &&
				VoiceConnectionManager.guildId === snapshot.guildId &&
				VoiceConnectionManager.channelId === snapshot.channelId
			)
		) {
			this.clearPreparedVoiceSessionRestore(snapshot);
		}
	}

	prepareVoiceSessionRestore(
		snapshot: VoiceSessionRestoreSnapshot,
		options?: {
			restoreVideo?: boolean;
			restoreStream?: boolean;
		},
	): void {
		this.transitionFacadeState({
			type: 'sessionRestore.prepare',
			guildId: snapshot.guildId,
			channelId: snapshot.channelId,
			restoreVideo: Boolean(options?.restoreVideo && snapshot.selfVideo),
			restoreStream: Boolean(options?.restoreStream && snapshot.selfStream),
		});
	}

	clearPreparedVoiceSessionRestore(snapshot?: Pick<VoiceSessionRestoreSnapshot, 'guildId' | 'channelId'>): void {
		if (!snapshot) {
			this.transitionFacadeState({type: 'sessionRestore.clear'});
			return;
		}
		this.transitionFacadeState({
			type: 'sessionRestore.clearTarget',
			guildId: snapshot.guildId,
			channelId: snapshot.channelId,
		});
	}

	private async stopActiveScreenShareForTeardown(context: 'disconnect' | 'channel_move'): Promise<void> {
		if (!LocalVoiceState.getSelfStream()) return;
		try {
			await VoiceMediaManager.setScreenShareEnabled(false, {sendUpdate: false, playSound: false});
		} catch (error) {
			logger.warn('Failed to stop screen share during teardown', {context, error});
		}
	}

	async disconnectFromVoiceChannel(reason: 'user' | 'error' | 'server' = 'user'): Promise<void> {
		if (this.disconnectPromise) {
			logger.debug('Voice teardown already in progress', {reason});
			return this.disconnectPromise;
		}
		const disconnectPromise = this.runDisconnectFromVoiceChannel(reason);
		this.disconnectPromise = disconnectPromise;
		try {
			await disconnectPromise;
		} finally {
			if (this.disconnectPromise === disconnectPromise) {
				this.disconnectPromise = null;
			}
		}
	}

	private async runDisconnectFromVoiceChannel(reason: 'user' | 'error' | 'server'): Promise<void> {
		if (this.nativeVoiceEngineEventDisposer) {
			this.nativeVoiceEngineEventDisposer();
			this.nativeVoiceEngineEventDisposer = null;
			this.nativeVoiceDeviceSyncDisposer?.();
			this.nativeVoiceDeviceSyncDisposer = null;
			this.nativeVoiceDataProtocolDisposer?.();
			this.nativeVoiceDataProtocolDisposer = null;
			NativeInboundVideoManager.clear();
			NativeVoiceE2EEStore.clear();
			NativeVoiceStatsStore.clear();
			try {
				await getVoiceEngine().disconnect();
			} catch (error) {
				logger.warn('Native voice engine disconnect failed', {error, reason});
			}
		}
		const {guildId, connectionId, connected, connecting, channelId} = VoiceConnectionManager.connectionState;
		if (!connected && !connecting && !channelId) return;
		logger.debug('Voice teardown starting', {guildId, channelId, reason});
		this.cancelPendingServerDisconnect();
		this.transitionFacadeState({type: 'disconnect.cleanupStarted', reason});
		await this.stopActiveScreenShareForTeardown('disconnect');
		this.clearViewerStreamKeys();
		this.stopTracking();
		this.statsManager.reset();
		if (reason === 'user' || reason === 'server') {
			VoiceSessionRestore.clearSnapshot();
		}
		if (reason !== 'server' && connectionId) {
			sendVoiceStateDisconnect(guildId, connectionId);
		}
		if (reason === 'user') {
			SoundCommands.playSound(SoundType.VoiceDisconnect);
		}
		VoiceCallLayout.reset();
		VoiceMediaStateCoordinator.resetLocalMediaState('disconnect');
		VoiceMediaManager.resetStreamTracking();
		VoiceParticipantManager.clear();
		this.voiceStateSync.reset();
		LocalVoiceState.clearConnectionState(connectionId);
		VoiceConnectionManager.disconnectFromVoiceChannel(reason);
		if (reason === 'user' || reason === 'server') {
			VoiceSessionRestore.clearSnapshot();
		}
		if (connectionId) CallMediaPrefs.clearForCall(connectionId);
		logger.info('Voice teardown complete', {channelId, reason});
		this.transitionFacadeState({type: 'cleanup.complete'});
	}

	private async disconnectForChannelMove(reason: 'user' | 'server'): Promise<void> {
		const {guildId, connectionId, connected, connecting, channelId} = VoiceConnectionManager.connectionState;
		if (!connected && !connecting && !channelId) return;
		logger.debug('Voice teardown for channel move', {guildId, channelId});
		this.cancelPendingServerDisconnect();
		this.transitionFacadeState({type: 'channelMove.cleanupStarted', reason});
		await this.stopActiveScreenShareForTeardown('channel_move');
		this.clearViewerStreamKeys();
		this.stopTracking();
		this.statsManager.reset();
		if (reason === 'user' && connectionId) {
			sendVoiceStateDisconnect(guildId, connectionId);
		}
		VoiceCallLayout.reset();
		VoiceMediaStateCoordinator.resetLocalMediaState('disconnect');
		VoiceMediaManager.resetStreamTracking();
		VoiceParticipantManager.clear();
		this.voiceStateSync.reset();
		LocalVoiceState.clearConnectionState(connectionId);
		VoiceConnectionManager.disconnectForChannelMove();
		if (connectionId) CallMediaPrefs.clearForCall(connectionId);
		SoundCommands.playSound(SoundType.UserMove);
		this.transitionFacadeState({type: 'cleanup.complete'});
	}

	private scheduleDeferredServerDisconnect(connectionId: string): void {
		this.cancelPendingServerDisconnect();
		this.transitionFacadeState({type: 'serverDisconnect.schedule', connectionId});
		this.pendingServerDisconnectTimeout = setTimeout(() => {
			if (
				shouldRunMediaEngineDeferredDisconnect(this.facadeSnapshot, {
					connectionId,
					currentConnectionId: VoiceConnectionManager.connectionId,
					connected: VoiceConnectionManager.connected,
				})
			) {
				logger.info('Deferred disconnect executing - no VOICE_SERVER_UPDATE received', {connectionId});
				void this.disconnectFromVoiceChannel('server');
			}
			this.pendingServerDisconnectTimeout = null;
			this.transitionFacadeState({type: 'serverDisconnect.timeoutElapsed', connectionId});
		}, DEFERRED_DISCONNECT_TIMEOUT_MS);
		logger.debug('Scheduled deferred disconnect', {connectionId, timeoutMs: DEFERRED_DISCONNECT_TIMEOUT_MS});
	}

	private cancelPendingServerDisconnect(): void {
		if (this.pendingServerDisconnectTimeout) {
			clearTimeout(this.pendingServerDisconnectTimeout);
			this.pendingServerDisconnectTimeout = null;
			logger.debug('Cancelled pending server disconnect', {
				connectionId: this.pendingServerDisconnectConnectionId,
			});
		}
		this.transitionFacadeState({type: 'serverDisconnect.cancel'});
	}

	private seedLocalVoiceStateFromServerUpdate(raw: VoiceServerUpdateData): void {
		const previousConnectionId = VoiceConnectionManager.connectionId;
		const currentVoiceState = raw.connection_id
			? VoiceStateManager.getVoiceStateByConnectionId(raw.connection_id)
			: null;
		const resolvedGuildId = raw.guild_id ?? VoiceConnectionManager.guildId ?? null;
		const resolvedChannelId = raw.channel_id ?? VoiceConnectionManager.channelId ?? null;
		if (!raw.connection_id) return;
		const viewerStreamKeys = currentVoiceState?.viewer_stream_keys ?? LocalVoiceState.getViewerStreamKeys();
		LocalVoiceState.seedConnectionState(raw.connection_id, {
			selfMute: currentVoiceState?.self_mute ?? LocalVoiceState.getSelfMute(),
			selfDeaf: currentVoiceState?.self_deaf ?? LocalVoiceState.getSelfDeaf(),
			selfVideo: currentVoiceState?.self_video ?? LocalVoiceState.getSelfVideo(),
			selfStream: currentVoiceState?.self_stream ?? LocalVoiceState.getSelfStream(),
			viewerStreamKeys: this.replaceViewerStreamConnectionId(
				viewerStreamKeys,
				resolvedGuildId,
				resolvedChannelId,
				previousConnectionId,
				raw.connection_id,
			),
		});
		const currentUserId = Users.getCurrentUser()?.id;
		if (currentUserId) {
			this.migratePinnedScreenShareIdentity(currentUserId, previousConnectionId, raw.connection_id);
		}
	}

	private connectViaNativeEngine(raw: VoiceServerUpdateData): void {
		const guildId = raw.guild_id ?? null;
		const channelId = raw.channel_id ?? null;
		const endpoint = raw.endpoint ?? null;
		const token = raw.token ?? null;
		if (!endpoint || !token || !channelId) {
			logger.warn('Native voice engine: ignoring VOICE_SERVER_UPDATE missing endpoint/token/channel', {
				hasEndpoint: !!endpoint,
				hasToken: !!token,
				hasChannel: !!channelId,
			});
			return;
		}
		this.seedLocalVoiceStateFromServerUpdate(raw);
		if (!VoiceConnectionManager.acceptNativeVoiceServerUpdate(raw)) {
			return;
		}
		const engine = getVoiceEngine();
		VoiceSubscriptionManager.cleanup();
		VoiceSubscriptionManager.setNativeEngine(engine);
		this.nativeVoiceEngineEventDisposer?.();
		this.nativeVoiceEngineEventDisposer = engine.onEvent((event) => {
			this.handleNativeVoiceEngineEvent(event, guildId, channelId);
		});
		this.bindNativeVoiceDataProtocols(engine);
		const bridge = window.electron?.voiceEngine;
		if (bridge) {
			NativeInboundVideoManager.start(bridge);
		}
		this.nativeVoiceDeviceSyncDisposer?.();
		this.nativeVoiceDeviceSyncDisposer = bindNativeVoiceDeviceSync({
			engine,
			getParticipants: () => VoiceParticipantManager.participants,
		});
		void engine
			.connect({url: endpoint, token, e2eeKey: raw.e2ee_key ?? null})
			.then(() => {
				VoiceConnectionManager.markConnected();
				this.navigateToVoiceChannel(guildId, channelId);
				logger.info('Native voice engine connect issued', {guildId, channelId, hasE2EE: !!raw.e2ee_key});
				void ScreenShareCodecNegotiation.publishLocalCapabilitiesNative('connected');
				void this.reconcileLocalAudioState('native voice engine connected').catch((error) => {
					logger.warn('Native voice engine microphone reconciliation failed after connect', {error});
				});
			})
			.catch((error) => {
				logger.error('Native voice engine connect failed', {error});
				suppressNativeVoiceEngineForFallback();
				this.nativeVoiceEngineEventDisposer?.();
				this.nativeVoiceEngineEventDisposer = null;
				this.nativeVoiceDeviceSyncDisposer?.();
				this.nativeVoiceDeviceSyncDisposer = null;
				this.nativeVoiceDataProtocolDisposer?.();
				this.nativeVoiceDataProtocolDisposer = null;
				VoiceSubscriptionManager.cleanup();
				NativeInboundVideoManager.clear();
				NativeVoiceE2EEStore.clear();
				NativeVoiceStatsStore.clear();
				void voiceDeviceManager
					.ensureDevices({requestPermissions: false, forceRefresh: true})
					.catch((refreshError) => {
						logger.warn('Failed to refresh browser voice devices before native fallback', {
							error: refreshError,
						});
					})
					.finally(() => {
						this.handleVoiceServerUpdateViaJs(raw);
					});
			});
	}

	private applyNativeLocalTrackState(source: VoiceTrackSource, enabled: boolean, eventName: string): void {
		if (source === VoiceTrackSource.ScreenShare) {
			VoiceMediaStateCoordinator.applyScreenShareState(enabled, {reason: 'server', sendUpdate: false});
		} else if (source === VoiceTrackSource.Camera) {
			VoiceMediaStateCoordinator.applyCameraState(enabled, {reason: 'server', sendUpdate: false});
		} else {
			logger.debug(`Native engine ${eventName}`, {source});
		}
	}

	private get nativeVoiceEngineEventManagers(): NativeVoiceEngineEventManagers {
		return {
			participants: {
				upsertParticipantFromNative: (fields) => VoiceParticipantManager.upsertParticipantFromNative(fields),
				patchParticipantTrackFlags: (identity, flags) =>
					VoiceParticipantManager.patchParticipantTrackFlags(identity, flags),
				setConnectionQualityForNative: (sid, quality) =>
					VoiceParticipantManager.setConnectionQualityForNative(sid, quality),
				updateActiveSpeakersBySid: (sids) => VoiceParticipantManager.updateActiveSpeakersBySid(sids),
				removeParticipant: (identity) => VoiceParticipantManager.removeParticipant(identity),
				removeParticipantBySid: (sid) => VoiceParticipantManager.removeParticipantBySid(sid),
			},
			inboundVideo: {
				registerTrack: (participantSid, trackSid, source) =>
					NativeInboundVideoManager.registerTrack(participantSid, trackSid, source),
				unregisterTrack: (trackSid) => NativeInboundVideoManager.unregisterTrack(trackSid),
				unregisterParticipant: (participantSid) => NativeInboundVideoManager.unregisterParticipant(participantSid),
			},
			localMedia: {
				onLocalTrackPublished: (source) => this.applyNativeLocalTrackState(source, true, 'localTrackPublished'),
				onLocalTrackUnpublished: (source) => this.applyNativeLocalTrackState(source, false, 'localTrackUnpublished'),
				onLocalTrackRepublished: (source) => this.applyNativeLocalTrackState(source, true, 'localTrackRepublished'),
			},
			e2ee: {
				setState: (sid, raw) => NativeVoiceE2EEStore.setState(sid, raw),
				remove: (sid) => NativeVoiceE2EEStore.remove(sid),
			},
			stats: {
				setStats: (stats) => NativeVoiceStatsStore.setStats(stats),
			},
		};
	}

	private getNativeRemoteParticipantIdentities(): Array<string> {
		return Object.values(VoiceParticipantManager.participants)
			.filter((participant) => !participant.isLocal)
			.map((participant) => participant.identity);
	}

	private bindNativeVoiceDataProtocols(engine: ReturnType<typeof getVoiceEngine>): void {
		this.nativeVoiceDataProtocolDisposer?.();
		const codecDisposer = ScreenShareCodecNegotiation.bindNative({
			publishData: (params) => engine.publishData(params),
			getRemoteParticipantIdentities: () => this.getNativeRemoteParticipantIdentities(),
		});
		this.nativeVoiceDataProtocolDisposer = () => {
			codecDisposer();
			ScreenShareCodecNegotiation.dispose();
			ScreenSharePublicationMigration.dispose();
		};
	}

	private bytesFromNativeDataPayload(payload: Record<string, unknown>): Uint8Array | null {
		const bytes = payload.payloadBytes;
		if (!Array.isArray(bytes)) return null;
		const output = new Uint8Array(bytes.length);
		for (let i = 0; i < bytes.length; i++) {
			const value = bytes[i];
			if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 255) return null;
			output[i] = value;
		}
		return output;
	}

	private handleNativeDataReceivedEvent(event: VoiceEngineEvent): boolean {
		if (event.type !== 'dataReceived') return false;
		const payload = event.payload as Record<string, unknown>;
		const participantIdentity = typeof payload.identity === 'string' ? payload.identity : null;
		const topic = typeof payload.topic === 'string' ? payload.topic : null;
		const bytes = this.bytesFromNativeDataPayload(payload);
		if (!participantIdentity || !topic || !bytes) return true;
		if (topic === SCREEN_SHARE_CODEC_NEGOTIATION_TOPIC) {
			ScreenShareCodecNegotiation.handleNativeDataMessage(participantIdentity, bytes);
		} else if (topic === SCREEN_SHARE_PUBLICATION_MIGRATION_TOPIC) {
			ScreenSharePublicationMigration.handleNativeDataMessage(participantIdentity, bytes);
		}
		return true;
	}

	private handleNativeParticipantProtocolEvent(event: VoiceEngineEvent): void {
		const payload = event.payload as Record<string, unknown>;
		const identity = typeof payload.identity === 'string' ? payload.identity : null;
		if (event.type === 'participantJoined') {
			ScreenShareCodecNegotiation.handleNativeParticipantConnected();
		} else if (event.type === 'participantLeft' && identity) {
			ScreenShareCodecNegotiation.handleNativeParticipantDisconnected(identity);
		}
	}

	private handleNativeRemoteTrackPublicationEvent(event: VoiceEngineEvent): void {
		if (event.type !== 'trackPublished') return;
		const payload = event.payload as Record<string, unknown>;
		const identity = typeof payload.identity === 'string' ? payload.identity : null;
		if (!identity) return;
		const source = asVoiceTrackSource(payload.source);
		if (source === VoiceTrackSource.Camera) {
			VoiceSubscriptionManager.reattachVideoAfterPublish(identity);
		} else if (source === VoiceTrackSource.ScreenShare) {
			VoiceSubscriptionManager.reattachScreenShareAfterPublish(identity);
		}
	}

	private handleNativeVoiceEngineEvent(event: VoiceEngineEvent, guildId: string | null, channelId: string): void {
		if (isFacadeOwnedConnectionEvent(event.type)) {
			const action = getNativeVoiceEngineConnectionEventAction(event);
			switch (action) {
				case 'connected':
					VoiceConnectionManager.markConnected();
					this.transitionFacadeState({type: 'connection.connected', guildId, channelId});
					break;
				case 'disconnected':
					void this.disconnectFromVoiceChannel('server');
					break;
				case 'reconnecting':
					AdaptiveScreenShareEngine.stop();
					this.statsManager.stopLatencyTracking();
					this.statsManager.stopStatsTracking();
					VoiceConnectionManager.markReconnecting();
					break;
				case 'reconnected':
					VoiceConnectionManager.markReconnected();
					this.statsManager.incrementReconnectionCount();
					this.statsManager.startLatencyTracking();
					this.statsManager.startStatsTracking();
					this.voiceStateSync.reset();
					void ScreenShareCodecNegotiation.publishLocalCapabilitiesNative('reconnected');
					void this.reconcileLocalAudioState('native voice engine reconnected');
					break;
				default:
					logger.debug('Native voice engine connection event ignored', {
						type: event.type,
						state: (event.payload as Record<string, unknown>).state,
					});
					break;
			}
			return;
		}
		if (this.handleNativeDataReceivedEvent(event)) return;
		mapNativeVoiceEngineEvent(event, this.nativeVoiceEngineEventManagers);
		this.handleNativeParticipantProtocolEvent(event);
		this.handleNativeRemoteTrackPublicationEvent(event);
	}

	handleVoiceServerUpdate(raw: VoiceServerUpdateData): void {
		if (raw.connection_id && raw.connection_id === this.pendingServerDisconnectConnectionId) {
			this.cancelPendingServerDisconnect();
		}
		if (isNativeVoiceEngineSelected()) {
			this.connectViaNativeEngine(raw);
			return;
		}
		if (isNativeVoiceEngineSelectionPending()) {
			void this.handleVoiceServerUpdateAfterNativeProbe(raw);
			return;
		}
		this.handleVoiceServerUpdateViaJs(raw);
	}

	private async handleVoiceServerUpdateAfterNativeProbe(raw: VoiceServerUpdateData): Promise<void> {
		if (await shouldUseNativeVoiceEngine()) {
			this.connectViaNativeEngine(raw);
			return;
		}
		this.handleVoiceServerUpdateViaJs(raw);
	}

	private handleVoiceServerUpdateViaJs(raw: VoiceServerUpdateData): void {
		const expectedChannelId = VoiceConnectionManager.channelId;
		const currentVoiceState = raw.connection_id
			? VoiceStateManager.getVoiceStateByConnectionId(raw.connection_id)
			: null;
		if (
			VoiceConnectionManager.connected &&
			raw.channel_id &&
			currentVoiceState?.channel_id &&
			currentVoiceState.channel_id !== raw.channel_id
		) {
			logger.warn('Ignoring VOICE_SERVER_UPDATE that conflicts with known voice state', {
				expectedChannelId,
				incomingChannelId: raw.channel_id,
				connectionId: raw.connection_id,
				voiceStateChannelId: currentVoiceState.channel_id,
			});
			return;
		}
		if (
			raw.channel_id &&
			expectedChannelId &&
			raw.channel_id !== expectedChannelId &&
			this.pendingUserMove?.channelId === expectedChannelId &&
			VoiceConnectionManager.connecting
		) {
			logger.warn('Ignoring VOICE_SERVER_UPDATE during user-initiated move', {
				expectedChannelId,
				incomingChannelId: raw.channel_id,
				connectionId: raw.connection_id,
			});
			return;
		}
		const resolvedGuildId = raw.guild_id ?? VoiceConnectionManager.guildId ?? null;
		const resolvedChannelId = raw.channel_id ?? VoiceConnectionManager.channelId ?? null;
		this.seedLocalVoiceStateFromServerUpdate(raw);
		const shouldPreserveLocalMedia =
			Boolean(this.facadeSnapshot.context.activeConnection) &&
			this.facadeSnapshot.context.activeConnection?.guildId === resolvedGuildId &&
			this.facadeSnapshot.context.activeConnection?.channelId === resolvedChannelId;
		const shouldApplyPendingSessionRestore =
			Boolean(this.pendingSessionRestore) &&
			this.pendingSessionRestore?.guildId === resolvedGuildId &&
			this.pendingSessionRestore?.channelId === resolvedChannelId;
		VoiceConnectionManager.handleVoiceServerUpdate(
			raw,
			(room, attemptId, guildId, channelId) => {
				bindRoomEvents(room, attemptId, guildId, channelId, {
					onConnected: async () => {
						this.transitionFacadeState({type: 'connection.connected', guildId, channelId});
						this.navigateToVoiceChannel(guildId, channelId);
						this.startTracking(room);
						if (shouldPreserveLocalMedia) {
							await this.restoreLocalMediaState(room);
						}
						if (shouldApplyPendingSessionRestore) {
							await this.restorePendingSessionMedia();
						}
						await this.reconcileLocalAudioState('voice room connected');
					},
					onDisconnected: () => {
						AdaptiveScreenShareEngine.stop();
						this.stopTracking();
						this.statsManager.reset();
					},
					onReconnecting: () => {
						AdaptiveScreenShareEngine.stop();
						this.statsManager.stopLatencyTracking();
						this.statsManager.stopStatsTracking();
					},
					onReconnected: () => {
						const room = VoiceConnectionManager.room;
						if (room?.localParticipant?.getTrackPublication(Track.Source.ScreenShare)?.videoTrack) {
							AdaptiveScreenShareEngine.start(room);
						}
						this.statsManager.incrementReconnectionCount();
						this.statsManager.startLatencyTracking();
						this.statsManager.startStatsTracking();
						this.voiceStateSync.reset();
						void this.reconcileLocalAudioState('voice room reconnected');
					},
				});
			},
			(isChannelMove, previousRoom) => {
				this.cancelPendingServerDisconnect();
				this.stopTracking();
				if (isChannelMove) {
					this.statsManager.reset();
				}
				VoiceParticipantManager.clear();
				this.voiceStateSync.reset();
				let shouldKeepPreviousRoomTracks = false;
				if (!isChannelMove && shouldPreserveLocalMedia && LocalVoiceState.getSelfStream()) {
					const pendingScreenShareReconnect = VoiceMediaManager.prepareScreenShareReconnect(previousRoom);
					if (pendingScreenShareReconnect) {
						this.transitionFacadeState({
							type: 'screenShareReconnect.prepare',
							snapshot: pendingScreenShareReconnect,
						});
						this.syncLocalVoiceStateWithServer({self_stream: true});
						shouldKeepPreviousRoomTracks = true;
					} else {
						this.transitionFacadeState({type: 'screenShareReconnect.clear'});
					}
				} else {
					this.transitionFacadeState({type: 'screenShareReconnect.clear'});
				}
				if (!shouldPreserveLocalMedia && this.facadeSnapshot.context.activeConnection) {
					VoiceMediaStateCoordinator.resetLocalMediaState('disconnect');
					VoiceMediaManager.resetStreamTracking();
				}
				return shouldKeepPreviousRoomTracks;
			},
			(_room, _attemptId) => {},
			(newRoom, attemptId, guildId, channelId) => {
				logger.info('Region hot-swap complete, rebinding tracking', {guildId, channelId, attemptId});
				this.stopTracking();
				this.startTracking(newRoom);
				VoiceParticipantManager.hydrateFromRoom(newRoom);
				VoiceRemoteSpeakingLevelManager.hydrateFromRoom(newRoom);
				VoicePermissionManager.applyDeafen(newRoom, getEffectiveAudioState().effectiveDeaf);
				VoiceMediaManager.applyAllLocalAudioPreferences(newRoom);
				if (newRoom.localParticipant?.getTrackPublication(Track.Source.ScreenShare)?.videoTrack) {
					AdaptiveScreenShareEngine.start(newRoom);
				} else {
					AdaptiveScreenShareEngine.stop();
				}
				if (guildId && channelId) {
					VoicePermissionManager.syncWithPermissionState(guildId, channelId, newRoom);
				}
				this.voiceStateSync.reset();
				void this.reconcileLocalAudioState('region hot-swap complete');
			},
		);
	}

	handleConnectionOpen(guilds: Array<GuildReadyData>): void {
		VoiceStateManager.handleConnectionOpen(guilds);
	}

	handleGuildCreate(guild: GuildReadyData): void {
		VoiceStateManager.handleGuildCreate(guild);
	}

	handleGuildDelete(guildId: string): void {
		VoiceStateManager.handleGuildDelete(guildId);
		if (VoiceConnectionManager.connected && VoiceConnectionManager.guildId === guildId) {
			void this.disconnectFromVoiceChannel('server');
		}
	}

	handlePassiveVoiceStates(guildId: string, voiceStates: Array<VoiceState>): void {
		for (const voiceState of voiceStates) {
			const stateWithGuild = {
				...voiceState,
				guild_id: voiceState.guild_id ?? guildId,
			};
			VoiceStateManager.handleGatewayVoiceStateUpdate(guildId, stateWithGuild);
		}
	}

	handleGatewayVoiceStateUpdate(guildId: string | null, voiceState: VoiceState): void {
		const user = Users.getCurrentUser();
		const isLocalConnection =
			user && voiceState.user_id === user.id && voiceState.connection_id === VoiceConnectionManager.connectionId;
		const previousConnectionState = voiceState.connection_id
			? VoiceStateManager.getVoiceStateByConnectionId(voiceState.connection_id)
			: null;
		const incomingViewerStreamKeys = normalizeViewerStreamKeys(voiceState.viewer_stream_keys);
		const previousLocalMediaState = isLocalConnection
			? {
					selfVideo: LocalVoiceState.getSelfVideo(),
					selfStream: LocalVoiceState.getSelfStream(),
				}
			: null;
		const previousLocalAudioState =
			isLocalConnection && previousConnectionState
				? {
						serverMute: isVoiceServerMuteActive(
							previousConnectionState,
							previousConnectionState.guild_id,
							previousConnectionState.channel_id,
						),
						serverDeaf: previousConnectionState.deaf,
						selfMute: previousConnectionState.self_mute,
						selfDeaf: previousConnectionState.self_deaf,
					}
				: null;
		const previousViewerStreamKeys: ReadonlyArray<string> = previousConnectionState?.viewer_stream_keys ?? [];
		if (!isLocalConnection && voiceState.connection_id) {
			this.migrateWatchedRemoteStreamConnection(guildId, voiceState);
		}
		VoiceStateManager.handleGatewayVoiceStateUpdate(guildId, voiceState);
		if (!isLocalConnection && VoiceConnectionManager.connected && voiceState.channel_id) {
			if (!areOrderedStringArraysEqual(previousViewerStreamKeys, incomingViewerStreamKeys)) {
				this.playSpectatorSounds(previousViewerStreamKeys, incomingViewerStreamKeys);
			}
		}
		if (!isLocalConnection && voiceState.connection_id) {
			const previousGuildId = normalizeStreamGuildId(previousConnectionState?.guild_id);
			const nextGuildId = normalizeStreamGuildId(guildId ?? voiceState.guild_id);
			const previousChannelId = previousConnectionState?.channel_id ?? null;
			const nextChannelId = voiceState.channel_id ?? null;
			const leftPreviousStreamChannel =
				previousChannelId != null && (previousChannelId !== nextChannelId || previousGuildId !== nextGuildId);
			const streamEnded = voiceState.self_stream === false;
			const streamChannelId = leftPreviousStreamChannel ? previousChannelId : nextChannelId;
			const streamGuildId = leftPreviousStreamChannel ? previousGuildId : nextGuildId;
			if (streamChannelId && (streamEnded || leftPreviousStreamChannel)) {
				const streamKey = getStreamKey(streamGuildId, streamChannelId, voiceState.connection_id);
				stopWatchingStreamKey(streamKey, {
					guildId: streamGuildId,
					channelId: streamChannelId,
				});
			}
		}
		if (isLocalConnection) {
			const currentConnectionId = VoiceConnectionManager.connectionId;
			const isCurrentConnection = voiceState.connection_id === currentConnectionId;
			let shouldApplyCurrentServerState = true;
			const serverPayload: VoiceStateSyncPayload | null =
				voiceState.channel_id && voiceState.connection_id
					? {
							guild_id: guildId,
							channel_id: voiceState.channel_id,
							connection_id: voiceState.connection_id,
							self_mute: voiceState.self_mute,
							self_deaf: voiceState.self_deaf,
							self_video: voiceState.self_video,
							self_stream: voiceState.self_stream,
							viewer_stream_keys: incomingViewerStreamKeys,
						}
					: null;
			if (voiceState.channel_id && voiceState.connection_id && isCurrentConnection) {
				const ptmActive = isPushToMuteActive();
				const pttActive = isPushToTalkActive();
				const applyServerState =
					serverPayload === null ? true : this.voiceStateSync.shouldApplyServerStateToLocalState(serverPayload);
				shouldApplyCurrentServerState = applyServerState;
				LocalVoiceState.syncConnectionState(voiceState.connection_id, {
					selfMute: ptmActive || pttActive || !applyServerState ? undefined : voiceState.self_mute,
					selfDeaf: applyServerState ? voiceState.self_deaf : undefined,
					selfVideo: applyServerState ? voiceState.self_video : undefined,
					selfStream: applyServerState ? voiceState.self_stream : undefined,
					viewerStreamKeys: applyServerState ? incomingViewerStreamKeys : undefined,
				});
			}
			this.voiceStateSync.confirmServerState(serverPayload);
			if (voiceState.channel_id && voiceState.connection_id) {
				if (isCurrentConnection && this.pendingServerDisconnectConnectionId === voiceState.connection_id) {
					this.cancelPendingServerDisconnect();
				}
				if (
					isCurrentConnection &&
					VoiceConnectionManager.connected &&
					!VoiceConnectionManager.connecting &&
					voiceState.channel_id !== VoiceConnectionManager.channelId
				) {
					const updatedGuildId = guildId ?? voiceState.guild_id ?? null;
					void this.disconnectForChannelMove('server').finally(() => {
						VoiceConnectionManager.recoverConnectionExpectation(updatedGuildId, voiceState.channel_id!);
					});
					return;
				}
				const shouldRecoverConnectionExpectation =
					!VoiceConnectionManager.connected &&
					!VoiceConnectionManager.connecting &&
					VoiceConnectionManager.guildId === null &&
					VoiceConnectionManager.channelId === null;
				if (shouldRecoverConnectionExpectation) {
					const recoveredGuildId = guildId ?? voiceState.guild_id ?? null;
					VoiceConnectionManager.recoverConnectionExpectation(recoveredGuildId, voiceState.channel_id);
					logger.info('Recovered connection expectation from voice state update', {
						guildId: recoveredGuildId,
						channelId: voiceState.channel_id,
						connectionId: voiceState.connection_id,
					});
				}
				const nextServerMute = isVoiceServerMuteActive(
					voiceState,
					guildId ?? voiceState.guild_id,
					voiceState.channel_id,
				);
				const audioStateChanged =
					previousLocalAudioState &&
					(previousLocalAudioState.serverMute !== nextServerMute ||
						previousLocalAudioState.serverDeaf !== voiceState.deaf ||
						previousLocalAudioState.selfMute !== voiceState.self_mute ||
						previousLocalAudioState.selfDeaf !== voiceState.self_deaf);
				if (isCurrentConnection && audioStateChanged) {
					void VoiceMediaManager.reconcileEffectiveAudioState(VoiceConnectionManager.room, {
						channelId: voiceState.channel_id,
						serverMute: nextServerMute,
						serverDeaf: voiceState.deaf,
					});
				}
			}
			if (previousLocalMediaState) {
				const videoDisabled =
					shouldApplyCurrentServerState && previousLocalMediaState.selfVideo && voiceState.self_video === false;
				const streamDisabled =
					shouldApplyCurrentServerState && previousLocalMediaState.selfStream && voiceState.self_stream === false;
				if (videoDisabled) {
					logger.info('Server disabled camera for local connection, unpublishing video track');
					void this.setCameraEnabled(false, {sendUpdate: false});
				}
				if (streamDisabled) {
					logger.info('Server disabled screen share for local connection, unpublishing stream track');
					void this.setScreenShareEnabled(false, {sendUpdate: false});
				}
			}
			if (
				voiceState.channel_id === null &&
				VoiceConnectionManager.connected &&
				voiceState.connection_id &&
				isCurrentConnection
			) {
				this.scheduleDeferredServerDisconnect(voiceState.connection_id);
			}
		}
	}

	handleGatewayVoiceStateDelete(guildId: string, userId: string): void {
		VoiceStateManager.handleGatewayVoiceStateDelete(guildId, userId);
	}

	getCurrentUserVoiceState(guildId?: string | null): NormalizedVoiceState | null {
		return VoiceStateManager.getCurrentUserVoiceState(
			guildId,
			Users.getCurrentUser()?.id,
			VoiceConnectionManager.connectionId,
		);
	}

	getVoiceState(guildId: string | null, userId?: string): NormalizedVoiceState | null {
		const currentUserId = Users.getCurrentUser()?.id;
		const connectionId = !userId || userId === currentUserId ? VoiceConnectionManager.connectionId : null;
		return VoiceStateManager.getVoiceState(guildId, userId, currentUserId, connectionId);
	}

	getVoiceStateByConnectionId(connectionId: string): NormalizedVoiceState | null {
		return VoiceStateManager.getVoiceStateByConnectionId(connectionId);
	}

	getAllVoiceStatesInChannel(guildId: string, channelId: string): Readonly<Record<string, NormalizedVoiceState>> {
		return VoiceStateManager.getAllVoiceStatesInChannel(guildId, channelId);
	}

	getAllVoiceStatesInGuild(
		guildId: string,
	): Readonly<Record<string, Readonly<Record<string, NormalizedVoiceState>>>> | undefined {
		return VoiceStateManager.getAllVoiceStatesInGuild(guildId);
	}

	getAllVoiceStates(): Readonly<
		Record<string, Readonly<Record<string, Readonly<Record<string, NormalizedVoiceState>>>>>
	> {
		return VoiceStateManager.getAllVoiceStates();
	}

	syncLocalVoiceStateWithServer(partial?: VoiceStateSyncPartial): void {
		LocalVoiceState.ensurePermissionMute();
		const {guildId, channelId, connectionId} = VoiceConnectionManager.connectionState;
		if (!channelId || !connectionId) return;
		const devicePermission = VoiceDevicePermissionState.getState().permissionStatus;
		const micGranted = MediaPermission.isMicrophoneGranted() || devicePermission === 'granted';
		const selfMute = resolveVoiceStateSelfMute({
			guildId,
			channelId,
			microphoneGranted: micGranted,
			requestedSelfMute: partial?.self_mute,
			effectiveSelfMute: getEffectiveSelfMuteForVoiceStatePayload(),
		});
		const payload: VoiceStateSyncPayload = {
			guild_id: guildId,
			channel_id: channelId,
			connection_id: connectionId,
			self_mute: selfMute,
			self_deaf: partial?.self_deaf ?? LocalVoiceState.getSelfDeaf(),
			self_video: partial?.self_video ?? LocalVoiceState.getSelfVideo(),
			self_stream: partial?.self_stream ?? LocalVoiceState.getSelfStream(),
			viewer_stream_keys: partial?.viewer_stream_keys ?? LocalVoiceState.getViewerStreamKeys(),
		};
		if (!micGranted && !LocalVoiceState.getSelfMute()) {
			LocalVoiceState.updateSelfMute(true);
		}
		this.voiceStateSync.requestState(payload);
	}

	private async reconcileLocalAudioState(reason: string): Promise<void> {
		const {channelId, connectionId} = VoiceConnectionManager.connectionState;
		if (!channelId || !connectionId) {
			logger.debug('Skipping local audio reconciliation without an active voice connection', {
				reason,
				channelId,
				connectionId,
			});
			return;
		}
		const serverVoiceState = VoiceStateManager.getVoiceStateByConnectionId(connectionId);
		const serverMute = isVoiceServerMuteActive(serverVoiceState, VoiceConnectionManager.guildId, channelId);
		const serverDeaf = serverVoiceState?.deaf ?? false;
		if (await shouldUseNativeVoiceEngine()) {
			await this.reconcileNativeEngineMicState({channelId, serverMute, serverDeaf});
			this.syncLocalVoiceStateWithServer();
			return;
		}
		await VoiceMediaManager.reconcileEffectiveAudioState(VoiceConnectionManager.room, {
			channelId,
			serverMute,
			serverDeaf,
		});
		this.syncLocalVoiceStateWithServer();
	}

	private async reconcileNativeEngineMicState(params: {
		channelId: string;
		serverMute: boolean;
		serverDeaf: boolean;
	}): Promise<void> {
		const audioState = getEffectiveAudioState({serverMute: params.serverMute, serverDeaf: params.serverDeaf});
		const pttActive = isPushToTalkActive();
		const ptmActive = isPushToMuteActive();
		const effectiveMute =
			audioState.effectiveDeaf ||
			audioState.serverMute ||
			audioState.selfMute ||
			(pttActive && !Keybind.pushToTalkHeld) ||
			(ptmActive && Keybind.pushToMuteHeld);
		const handled = await this.setMicEnabledViaEngine(!effectiveMute);
		if (!handled) {
			logger.debug('Native engine did not handle mic enable; flag toggled off mid-reconcile', {
				channelId: params.channelId,
				effectiveMute,
			});
		}
	}

	getParticipantByUserIdAndConnectionId(
		userId: string,
		connectionId: string | null,
	): LivekitParticipantSnapshot | undefined {
		return VoiceParticipantManager.getParticipantByUserIdAndConnectionId(userId, connectionId);
	}

	upsertParticipant(participant: Participant): void {
		VoiceParticipantManager.upsertParticipant(participant);
	}

	async setCameraEnabled(
		enabled: boolean,
		options?: {
			deviceId?: string;
			sendUpdate?: boolean;
		},
	): Promise<void> {
		if (await shouldUseNativeVoiceEngine()) {
			await this.setCameraEnabledViaEngine(enabled, options);
			return;
		}
		await VoiceMediaManager.setCameraEnabled(enabled, options);
	}

	private async getNativeCameraPublishParams(options?: {deviceId?: string}): Promise<{
		deviceId?: string;
		width: number;
		height: number;
		frameRate: number;
	}> {
		const resolution = VoiceSettings.getCameraResolution();
		const preset =
			resolution === 'high'
				? {width: 1920, height: 1080, frameRate: 30}
				: resolution === 'medium'
					? {width: 1280, height: 720, frameRate: 30}
					: {width: 640, height: 360, frameRate: 24};
		const deviceId = options?.deviceId ?? VoiceSettings.getVideoDeviceId();
		const nativeDeviceId = await resolveNativeCameraDeviceId(deviceId);
		return {
			...preset,
			...(nativeDeviceId ? {deviceId: nativeDeviceId} : {}),
		};
	}

	private async setCameraEnabledViaEngine(
		enabled: boolean,
		options?: {
			deviceId?: string;
			sendUpdate?: boolean;
		},
	): Promise<void> {
		const sendUpdate = options?.sendUpdate ?? true;
		try {
			if (enabled) {
				const nativeResult = await ensureNativePermission('camera');
				if (nativeResult === 'denied') {
					MediaPermission.markCameraExplicitlyDenied();
					VoiceMediaStateCoordinator.applyCameraState(false, {reason: sendUpdate ? 'user' : 'server', sendUpdate});
					return;
				}
				if (nativeResult === 'granted') {
					MediaPermission.updateCameraPermissionGranted();
				}
				await getVoiceEngine().publishCamera(await this.getNativeCameraPublishParams(options));
				VoiceMediaStateCoordinator.applyCameraState(true, {reason: sendUpdate ? 'user' : 'server', sendUpdate});
				SoundCommands.playSound(SoundType.CameraOn);
			} else {
				VoiceMediaStateCoordinator.applyCameraState(false, {reason: sendUpdate ? 'user' : 'server', sendUpdate});
				await getVoiceEngine().unpublishCamera();
				SoundCommands.playSound(SoundType.CameraOff);
			}
			logger.info('Native voice engine camera state updated', {enabled});
		} catch (error) {
			logger.warn('Native voice engine camera state update failed', {enabled, error});
			VoiceMediaStateCoordinator.applyCameraState(false, {reason: sendUpdate ? 'user' : 'server', sendUpdate});
			SoundCommands.playSound(SoundType.CameraOff);
			throw error;
		}
	}

	async setScreenShareEnabled(
		enabled: boolean,
		options?: ScreenShareCaptureOptions & {
			sendUpdate?: boolean;
			playSound?: boolean;
			restartIfEnabled?: boolean;
		},
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		await VoiceMediaManager.setScreenShareEnabled(enabled, options, publishOptions);
	}

	async startDeviceScreenShare(
		options?: DeviceScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		await VoiceMediaManager.startDeviceScreenShare(options, publishOptions);
	}

	async publishScreenShareViaEngine(params: {
		captureId: string;
		width: number;
		height: number;
		codec?: '' | 'vp8' | 'vp9' | 'h264' | 'h265' | 'av1';
	}): Promise<boolean> {
		if (!(await shouldUseNativeVoiceEngine())) return false;
		await getVoiceEngine().publishScreenShare(params);
		return true;
	}

	async unpublishScreenShareViaEngine(): Promise<boolean> {
		if (!(await shouldUseNativeVoiceEngine())) return false;
		await getVoiceEngine().unpublishScreenShare();
		return true;
	}

	async setMicEnabledViaEngine(enabled: boolean): Promise<boolean> {
		if (!(await shouldUseNativeVoiceEngine())) return false;
		if (enabled) {
			await getVoiceEngine().publishMicrophone({
				deviceId: VoiceSettings.getInputDeviceId(),
				echoCancellation: VoiceSettings.getEchoCancellation(),
				noiseSuppression: VoiceSettings.getNoiseSuppression(),
				autoGainControl: VoiceSettings.getAutoGainControl(),
			});
		}
		await getVoiceEngine().setMicEnabled(enabled);
		return true;
	}

	async replaceActiveDisplayScreenShare(
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		return VoiceMediaManager.replaceActiveDisplayScreenShare(options, publishOptions);
	}

	async replaceActiveDeviceScreenShare(
		options?: DeviceScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		return VoiceMediaManager.replaceActiveDeviceScreenShare(options, publishOptions);
	}

	async startNativeDisplayScreenShare(
		nativeOptions: NativeScreenShareOptions,
		options?: {sendUpdate?: boolean; playSound?: boolean},
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		await VoiceMediaManager.startNativeDisplayScreenShare(nativeOptions, options, publishOptions);
	}

	async replaceActiveNativeDisplayScreenShare(
		nativeOptions: NativeScreenShareOptions,
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		return VoiceMediaManager.replaceActiveNativeDisplayScreenShare(nativeOptions, options, publishOptions);
	}

	async ensureLinuxScreenShareAudioPublication(
		linuxRule?: NonNullable<NativeAudioStartOptions['linuxRule']>,
		options?: {includeSelfWindowAudio?: boolean; replaceExisting?: boolean},
	): Promise<boolean> {
		return VoiceMediaManager.ensureLinuxScreenShareAudioPublication(linuxRule, options);
	}

	setScreenShareAudioMuted(muted: boolean): void {
		VoiceMediaManager.setScreenShareAudioMuted(muted);
	}

	async updateActiveScreenShareSettings(
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		return VoiceMediaManager.updateActiveScreenShareSettings(options, publishOptions);
	}

	applyLocalAudioPreferencesForUser(userId: string): void {
		VoiceMediaManager.applyLocalAudioPreferencesForUser(userId, this.room);
	}

	applyAllLocalAudioPreferences(): void {
		VoiceMediaManager.applyAllLocalAudioPreferences(this.room);
	}

	applyLocalInputVolume(): void {
		VoiceMediaManager.applyLocalInputVolume(this.room);
	}

	setLocalVideoDisabled(identity: string, disabled: boolean): void {
		VoiceMediaManager.setLocalVideoDisabled(identity, disabled, this.room, this.connectionId);
	}

	applyPushToTalkHold(held: boolean): void {
		VoiceMediaManager.applyPushToTalkHold(held, this.room, () => this.getCurrentUserVoiceState());
	}

	applyPushToMuteHold(held: boolean): void {
		VoiceMediaManager.applyPushToMuteHold(held, this.room, () => this.getCurrentUserVoiceState());
	}

	handlePushToTalkModeChange(): void {
		VoiceMediaManager.handlePushToTalkModeChange(this.room, () => this.getCurrentUserVoiceState());
	}

	getMuteReason(voiceState: VoiceState | null): VoiceMuteReason {
		return VoiceMediaManager.getMuteReason(voiceState, this.guildId, this.channelId);
	}

	async toggleCameraFromKeybind(): Promise<void> {
		await VoiceMediaManager.toggleCameraFromKeybind();
	}

	async toggleScreenShareFromKeybind(): Promise<void> {
		await VoiceMediaManager.toggleScreenShareFromKeybind();
	}

	private startTracking(roomOverride?: Room | null): void {
		const room = roomOverride ?? VoiceConnectionManager.room;
		if (!room) {
			logger.warn('No room available');
			return;
		}
		this.statsManager.setRoom(room);
		this.statsManager.startLatencyTracking();
		this.statsManager.startStatsTracking();
		VoiceSubscriptionManager.setRoom(room);
		VoicePermissionManager.initializeSubscriptions(room);
		this.outputDeviceSyncDisposer?.();
		this.outputDeviceSyncDisposer = bindOutputDeviceSync(room);
		this.audioPreferencesSyncDisposer?.();
		this.audioPreferencesSyncDisposer = this.bindAudioPreferencesSync(room);
		this.startAfkTracking();
		const channelId = VoiceConnectionManager.channelId;
		if (channelId) {
			void VoiceDebugLoggingManager.start({
				guildId: VoiceConnectionManager.guildId,
				channelId,
				connectionId: VoiceConnectionManager.connectionId,
				room,
				collectSnapshot: () => this.createVoiceDiagnosticsSnapshot(),
			});
		}
		logger.info('All tracking started');
	}

	private stopTracking(): void {
		void VoiceDebugLoggingManager.stop('tracking-stopped');
		this.statsManager.stopLatencyTracking();
		this.statsManager.stopStatsTracking();
		VoiceRemoteSpeakingLevelManager.clear();
		VoiceSubscriptionManager.cleanup();
		VoicePermissionManager.reset();
		this.outputDeviceSyncDisposer?.();
		this.outputDeviceSyncDisposer = null;
		this.audioPreferencesSyncDisposer?.();
		this.audioPreferencesSyncDisposer = null;
		this.stopAfkTracking();
		logger.info('All tracking stopped');
	}

	private async createVoiceDiagnosticsSnapshot(): Promise<Record<string, unknown>> {
		await Promise.allSettled([loadGpuEncoderReport(), loadOpenH264Status(), loadVideoDecoderExclusions()]);
		return {
			connection: {
				guildId: VoiceConnectionManager.guildId,
				channelId: VoiceConnectionManager.channelId,
				connectionId: VoiceConnectionManager.connectionId,
				connected: VoiceConnectionManager.connected,
				connecting: VoiceConnectionManager.connecting,
				reconnecting: VoiceConnectionManager.reconnecting,
				disconnecting: VoiceConnectionManager.disconnecting,
				voiceServerEndpoint: VoiceConnectionManager.voiceServerEndpoint,
				regionHotSwapInProgress: VoiceConnectionManager.regionHotSwapInProgress,
				reconnectAttempts: VoiceConnectionManager.reconnectAttempts,
				nativeVoiceEngineSelected: isNativeVoiceEngineSelected(),
			},
			localVoiceState: {
				selfMute: LocalVoiceState.getSelfMute(),
				selfDeaf: LocalVoiceState.getSelfDeaf(),
				selfVideo: LocalVoiceState.getSelfVideo(),
				selfStream: LocalVoiceState.getSelfStream(),
				selfStreamAudio: LocalVoiceState.getSelfStreamAudio(),
				selfStreamAudioMute: LocalVoiceState.getSelfStreamAudioMute(),
				viewerStreamKeys: LocalVoiceState.getViewerStreamKeys(),
				hasUserSetMute: LocalVoiceState.getHasUserSetMute(),
				hasUserSetDeaf: LocalVoiceState.getHasUserSetDeaf(),
			},
			effectiveAudioState: getEffectiveAudioState(),
			connectionVoiceStates: VoiceStateManager.getConnectionVoiceStates(),
			participants: Object.values(VoiceParticipantManager.participants),
			stats: {
				currentLatency: this.currentLatency,
				averageLatency: this.averageLatency,
				displayLatency: this.displayLatency,
				estimatedLatency: this.estimatedLatency,
				reconnectionCount: this.reconnectionCount,
				voiceStats: this.voiceStats,
				perTrackStats: this.perTrackStats,
				statsTimeSeriesTail: this.statsTimeSeries.slice(-30),
				latencyHistoryTail: this.latencyHistory.slice(-30),
				publisherTransport: this.publisherTransport,
				subscriberTransport: this.subscriberTransport,
			},
			screenShare: {
				selectedCodec: ScreenShareCodecNegotiation.getSelectedCodec(),
				preferenceOrder: getScreenShareCodecPreferenceOrder(),
				capture: await getScreenShareCaptureDiagnosticSnapshot(),
				audioCapture: await createVoiceAudioDiagnosticsSnapshot(),
				codecCapabilities: {
					localAdvertisements: buildLocalCodecAdvertisements(),
					report: getCodecCapabilityReport(),
					gpuEncoderReport: getGpuEncoderReportSync(),
					openH264Status: getOpenH264StatusSync(),
					decoderExclusions: getVideoDecoderExclusionsSync(),
				},
				adaptiveQuality: AdaptiveScreenShareEngine.qualitySnapshot,
				settings: {
					preferredScreenShareCodec: VoiceSettings.getPreferredScreenShareCodec(),
					screenShareEncoderMode: VoiceSettings.getScreenShareEncoderMode(),
					screenShareSoftwareQuality: VoiceSettings.getScreenShareSoftwareQuality(),
					screenShareScalabilityMode: VoiceSettings.getScreenShareScalabilityMode(),
					screenShareBackupCodecMode: VoiceSettings.getScreenShareBackupCodecMode(),
					screenShareMaxBitrateMbps: VoiceSettings.getScreenShareMaxBitrateMbps(),
					adaptiveScreenShareQuality: VoiceSettings.getAdaptiveScreenShareQuality(),
					screenshareResolution: VoiceSettings.getScreenshareResolution(),
					videoFrameRate: VoiceSettings.getVideoFrameRate(),
					streamingMode: VoiceSettings.getStreamingMode(),
					shareAppAudio: VoiceSettings.getShareAppAudio(),
					shareDesktopAudio: VoiceSettings.getShareDesktopAudio(),
					shareDeviceAudio: VoiceSettings.getShareDeviceAudio(),
					screenShareAudioSourceMode: VoiceSettings.getScreenShareAudioSourceMode(),
					screenShareAudioIncludeSources: VoiceSettings.getScreenShareAudioIncludeSources(),
					screenShareAudioExcludeSources: VoiceSettings.getScreenShareAudioExcludeSources(),
				},
			},
		};
	}

	private abortVoiceConnection(): void {
		this.transitionFacadeState({type: 'screenShareReconnect.clear'});
		VoiceConnectionManager.abortConnection();
	}

	private bindAudioPreferencesSync(room: Room): () => void {
		return reaction(
			() => ({
				inputVolume: VoiceSettings.inputVolume,
				outputVolume: VoiceSettings.outputVolume,
				participantVolumes: ParticipantVolume.volumes,
				participantMutes: ParticipantVolume.localMutes,
				connectionVolumes: ParticipantVolume.connectionVolumesByLocalConnectionId,
				streamAudioRevision: StreamAudioPrefs.audioPrefsRevision,
			}),
			(current, previous) => {
				if (current.inputVolume !== previous.inputVolume) {
					VoiceMediaManager.applyLocalInputVolume(room);
				}
				if (
					current.outputVolume !== previous.outputVolume ||
					current.participantVolumes !== previous.participantVolumes ||
					current.participantMutes !== previous.participantMutes ||
					current.connectionVolumes !== previous.connectionVolumes ||
					current.streamAudioRevision !== previous.streamAudioRevision
				) {
					VoiceMediaManager.applyAllLocalAudioPreferences(room);
				}
			},
		);
	}

	private startAfkTracking(): void {
		this.stopAfkTracking();
		this.afkIntervalId = setInterval(() => {
			if (!VoiceConnectionManager.connected || !VoiceConnectionManager.guildId || !VoiceConnectionManager.channelId)
				return;
			if (this.noteCurrentLocalVoiceActivity()) return;
			const guild = Guilds.getGuild(VoiceConnectionManager.guildId);
			if (
				shouldMoveToGuildAfkChannel({
					channelId: VoiceConnectionManager.channelId,
					afkChannelId: guild?.afkChannelId,
					afkTimeoutSeconds: guild?.afkTimeout,
					inactiveDurationMs: Idle.getInactiveDurationMs(),
				})
			) {
				void this.moveToAfkChannel();
			}
		}, AFK_CHECK_INTERVAL_MS);
	}

	private noteCurrentLocalVoiceActivity(): boolean {
		const livekitParticipant = VoiceConnectionManager.room?.localParticipant;
		if (noteLocalVoiceActivity(livekitParticipant)) return true;
		return noteLocalVoiceActivityFromSnapshot(VoiceParticipantManager.getLocalParticipant());
	}

	private stopAfkTracking(): void {
		if (this.afkIntervalId !== null) {
			clearInterval(this.afkIntervalId);
			this.afkIntervalId = null;
		}
	}

	private playSpectatorSounds(oldStreamKeys: ReadonlyArray<string>, newStreamKeys: ReadonlyArray<string>): void {
		const myConnectionId = VoiceConnectionManager.connectionId;
		if (!myConnectionId) return;
		const myViewerStreamKeys = LocalVoiceState.getViewerStreamKeys();
		const isRelevantStream = (key: string): boolean => {
			const parsed = parseStreamKey(key);
			if (!parsed) return false;
			if (parsed.connectionId === myConnectionId) return true;
			if (myViewerStreamKeys.includes(key)) return true;
			return false;
		};
		const isEndedLocalStream = (key: string): boolean => {
			const parsed = parseStreamKey(key);
			return parsed?.connectionId === myConnectionId && !LocalVoiceState.getSelfStream();
		};
		const oldRelevant = new Set(oldStreamKeys.filter(isRelevantStream));
		const newRelevant = new Set(newStreamKeys.filter(isRelevantStream));
		const joined = newStreamKeys.filter((k) => isRelevantStream(k) && !oldRelevant.has(k));
		const left = oldStreamKeys.filter((k) => isRelevantStream(k) && !newRelevant.has(k) && !isEndedLocalStream(k));
		if (joined.length > 0) {
			SoundCommands.playSound(SoundType.ViewerJoin);
		} else if (left.length > 0) {
			SoundCommands.playSound(SoundType.ViewerLeave);
		}
	}

	private replaceViewerStreamConnectionId(
		keys: ReadonlyArray<string>,
		guildId: string | null | undefined,
		channelId: string | null | undefined,
		previousConnectionId: string | null | undefined,
		nextConnectionId: string | null | undefined,
	): Array<string> {
		if (!channelId || !previousConnectionId || !nextConnectionId || previousConnectionId === nextConnectionId) {
			return [...keys];
		}
		const normalizedGuildId = normalizeStreamGuildId(guildId);
		const previousKey = getStreamKey(normalizedGuildId, channelId, previousConnectionId);
		const nextKey = getStreamKey(normalizedGuildId, channelId, nextConnectionId);
		let changed = false;
		const updated: Array<string> = [];
		for (const key of keys) {
			const migratedKey = key === previousKey ? nextKey : key;
			changed ||= migratedKey !== key;
			if (!updated.includes(migratedKey)) {
				updated.push(migratedKey);
			}
		}
		return changed ? updated : [...keys];
	}

	private migratePinnedScreenShareIdentity(
		userId: string,
		previousConnectionId: string | null | undefined,
		nextConnectionId: string | null | undefined,
	): void {
		if (!previousConnectionId || !nextConnectionId || previousConnectionId === nextConnectionId) return;
		if (VoiceCallLayout.pinnedParticipantSource !== VoiceTrackSource.ScreenShare) return;
		if (VoiceCallLayout.pinnedParticipantIdentity !== `user_${userId}_${previousConnectionId}`) return;
		VoiceCallLayout.setPinnedParticipant(`user_${userId}_${nextConnectionId}`, VoiceTrackSource.ScreenShare);
	}

	private migrateWatchedRemoteStreamConnection(guildId: string | null, voiceState: VoiceState): void {
		const channelId = voiceState.channel_id;
		const nextConnectionId = voiceState.connection_id;
		if (!channelId || !nextConnectionId || !voiceState.self_stream) return;
		const normalizedGuildId = normalizeStreamGuildId(guildId ?? voiceState.guild_id);
		const previousStates = VoiceStateManager.getAllVoiceStatesInChannel(normalizedGuildId ?? ME, channelId);
		const currentKeys = LocalVoiceState.getViewerStreamKeys();
		let updatedKeys: Array<string> = [...currentKeys];
		let migrated = false;
		for (const previousState of Object.values(previousStates)) {
			const previousConnectionId = previousState.connection_id;
			if (
				previousState.user_id !== voiceState.user_id ||
				!previousConnectionId ||
				previousConnectionId === nextConnectionId ||
				!previousState.self_stream
			) {
				continue;
			}
			const nextKeys = this.replaceViewerStreamConnectionId(
				updatedKeys,
				normalizedGuildId,
				channelId,
				previousConnectionId,
				nextConnectionId,
			);
			if (JSON.stringify(nextKeys) !== JSON.stringify(updatedKeys)) {
				this.migratePinnedScreenShareIdentity(voiceState.user_id, previousConnectionId, nextConnectionId);
				updatedKeys = nextKeys;
				migrated = true;
			}
		}
		const result = replaceWatchedStreamKeys(updatedKeys, {sync: false});
		if (!result.changed) return;
		this.syncLocalVoiceStateWithServer({viewer_stream_keys: result.keys});
		if (migrated) {
			logger.info('Migrated watched stream connection id', {
				userId: voiceState.user_id,
				channelId,
				nextConnectionId,
			});
		}
	}

	disconnectRemoteDevice(guildId: string, connectionId: string): void {
		sendVoiceStateDisconnect(guildId, connectionId);
	}

	disconnectAllRemoteDevices(
		devices: ReadonlyArray<{
			guildId: string;
			connectionId: string;
		}>,
	): void {
		for (const device of devices) {
			sendVoiceStateDisconnect(device.guildId, device.connectionId);
		}
	}

	async moveToAfkChannel(): Promise<void> {
		const {guildId, channelId, connected} = VoiceConnectionManager.connectionState;
		if (!connected || !guildId || !channelId) return;
		const guild = Guilds.getGuild(guildId);
		if (!guild?.afkChannelId || channelId === guild.afkChannelId) return;
		SoundCommands.playSound(SoundType.UserMove);
		await this.connectToVoiceChannel(guildId, guild.afkChannelId);
	}

	getLastConnectedChannel(): {
		guildId: string;
		channelId: string;
	} | null {
		return VoiceConnectionManager.lastConnectedChannel;
	}

	getShouldReconnect(): boolean {
		return VoiceConnectionManager.shouldAutoReconnect;
	}

	markReconnectionAttempted(): void {
		VoiceConnectionManager.markReconnectionAttempted();
	}

	async handleLogout(): Promise<void> {
		this.cancelPendingServerDisconnect();
		this.transitionFacadeState({type: 'cleanup.logoutStarted'});
		this.clearViewerStreamKeys();
		this.stopTracking();
		await this.stopActiveScreenShareForTeardown('disconnect');
		VoiceSessionRestore.clearSnapshot();
		VoiceConnectionManager.cleanup();
		VoiceStateManager.clearAllVoiceStates();
		VoiceParticipantManager.clear();
		VoicePermissionManager.reset();
		VoiceMediaManager.resetStreamTracking();
		VoiceMediaStateCoordinator.resetLocalMediaState('logout');
		this.voiceStateSync.reset();
		try {
			await voiceStatsDB.clear();
		} catch (error) {
			logger.error('Failed to clear voice stats DB during logout', error);
			throw error;
		} finally {
			logger.info('Cleanup complete');
			this.transitionFacadeState({type: 'cleanup.complete'});
		}
	}

	handleGatewayError(error: GatewayErrorData): void {
		const decision = selectMediaEngineGatewayErrorDecision(this.facadeSnapshot, {
			code: error.code,
			connecting: VoiceConnectionManager.connecting,
			connected: VoiceConnectionManager.connected,
			channelId: VoiceConnectionManager.channelId,
		});
		if (decision.type === 'ignore') return;
		logger.warn(`Voice-related gateway error: [${error.code}] ${error.message}`);
		if (decision.clearPendingSessionRestore) {
			this.transitionFacadeState({type: 'sessionRestore.clear'});
		}
		if (decision.unavailableChannelId) {
			this.clearUnavailableVoiceTarget(decision.unavailableChannelId, 'gateway-error', {
				showToast: decision.showUnavailableToast,
			});
		}
		if (decision.clearViewerStreamKeys) {
			this.clearViewerStreamKeys();
		}
		if (decision.abortConnection) {
			logger.info('Gateway voice error while connecting, aborting', {code: error.code});
			this.abortVoiceConnection();
		}
		if (decision.disconnectReason) {
			void this.disconnectFromVoiceChannel(decision.disconnectReason);
		}
		if (decision.toast) {
			if (!this.i18n) {
				throw new Error('MediaEngineFacade: i18n not initialized');
			}
			const descriptor =
				decision.toast === 'timed-out'
					? YOU_CAN_T_JOIN_WHILE_YOU_RE_ON_DESCRIPTOR
					: decision.toast === 'connection-limit'
						? VOICE_CONNECTION_LIMIT_REACHED_DESCRIPTOR
						: CLAIM_YOUR_ACCOUNT_TO_JOIN_THIS_VOICE_CHANNEL_DESCRIPTOR;
			this.showVoiceErrorModal(
				this.i18n._(descriptor),
				`voice.media-engine-facade.gateway-${decision.toast}-error-modal`,
			);
		}
	}

	cleanup(): void {
		this.cancelPendingServerDisconnect();
		this.transitionFacadeState({type: 'cleanup.cleanupStarted'});
		this.clearViewerStreamKeys();
		this.stopTracking();
		this.statsManager.cleanup();
		VoiceSubscriptionManager.cleanup();
		VoicePermissionManager.reset();
		VoiceMediaManager.resetStreamTracking();
		VoiceConnectionManager.cleanup();
		VoiceStateManager.clearAllVoiceStates();
		VoiceParticipantManager.clear();
		VoiceMediaStateCoordinator.resetLocalMediaState('cleanup');
		this.voiceStateSync.reset();
		this.transitionFacadeState({type: 'cleanup.complete'});
	}

	reset(): void {
		this.cancelPendingServerDisconnect();
		this.clearViewerStreamKeys();
		this.statsManager.reset();
		this.transitionFacadeState({type: 'cleanup.reset'});
		VoiceConnectionManager.resetConnectionState();
		VoiceConnectionManager.resetReconnectState();
		VoiceRemoteSpeakingLevelManager.clear();
		VoicePermissionManager.reset();
		VoiceMediaManager.resetStreamTracking();
		VoiceParticipantManager.clear();
		VoiceMediaStateCoordinator.resetLocalMediaState('cleanup');
		this.voiceStateSync.reset();
	}

	private async restoreLocalMediaState(roomOverride?: Room | null): Promise<void> {
		const room = roomOverride ?? VoiceConnectionManager.room;
		const participant = room?.localParticipant ?? null;
		if (!participant) return;
		if (LocalVoiceState.getSelfVideo() && !participant.isCameraEnabled) {
			await VoiceMediaManager.setCameraEnabled(true, {
				deviceId: VoiceSettings.getVideoDeviceId(),
				sendUpdate: false,
			});
		}
		const pendingScreenShareReconnect = this.pendingScreenShareReconnect;
		this.transitionFacadeState({type: 'screenShareReconnect.consume'});
		const shouldRestoreScreenShare =
			(LocalVoiceState.getSelfStream() || pendingScreenShareReconnect !== null) && !participant.isScreenShareEnabled;
		if (shouldRestoreScreenShare && pendingScreenShareReconnect) {
			const restored = await VoiceMediaManager.restoreScreenShareReconnect(room, pendingScreenShareReconnect);
			if (!restored) {
				VoiceMediaStateCoordinator.applyScreenShareState(false, {reason: 'server', sendUpdate: false});
			}
			return;
		}
		if (shouldRestoreScreenShare) {
			try {
				await VoiceMediaManager.setScreenShareEnabled(true, {sendUpdate: false});
			} catch (error) {
				logger.warn('Failed to restore screen share', {error});
				VoiceMediaStateCoordinator.applyScreenShareState(false, {reason: 'server', sendUpdate: false});
			}
		}
	}

	private async restorePendingSessionMedia(): Promise<void> {
		const pendingRestore = this.pendingSessionRestore;
		this.transitionFacadeState({type: 'sessionRestore.consume'});
		if (!pendingRestore) {
			return;
		}
		if (pendingRestore.restoreVideo) {
			try {
				await VoiceMediaManager.setCameraEnabled(true, {
					deviceId: VoiceSettings.getVideoDeviceId(),
					sendUpdate: false,
				});
			} catch (error) {
				logger.warn('Failed to restore camera from startup voice restore', {error});
			}
		}
		if (!pendingRestore.restoreStream) {
			return;
		}
		try {
			await VoiceMediaManager.setScreenShareEnabled(true, {sendUpdate: false});
		} catch (error) {
			logger.warn('Failed to restore screen share from startup voice restore', {error});
			VoiceMediaStateCoordinator.applyScreenShareState(false, {reason: 'server', sendUpdate: false});
		}
		if (!LocalVoiceState.getSelfStream() && this.i18n) {
			ToastCommands.createToast({
				type: 'info',
				children: this.i18n._(RECONNECT_SUCCEEDED_PICK_A_SCREEN_AGAIN_IF_YOU_DESCRIPTOR),
			});
		}
	}

	private navigateToVoiceChannel(guildId: string | null, channelId: string): void {
		const targetGuildId = guildId ?? ME;
		NavigationCommands.selectChannel(targetGuildId, channelId);
	}

	private clearViewerStreamKeys(): void {
		if (LocalVoiceState.getViewerStreamKeys().length === 0) {
			return;
		}
		replaceWatchedStreamKeys([], {sync: false});
	}
}

const instance = new MediaEngineFacade();

(
	window as typeof window & {
		_mediaEngineFacade?: MediaEngineFacade;
	}
)._mediaEngineFacade = instance;

export default instance;
